/*
 * TEMPORARY, FOR TESTING — set back to true to restore the cart line.
 *
 * When false, a free gift is never written to the Shopify cart, whoever picks it. The shopper
 * still chooses in the drawer and still sees the gift; only the /cart/add.js call is suppressed,
 * and the choice is kept in localStorage instead.
 *
 * Why: our gift line is a real ₹149 product to Shopflo, not a gift, because MYSTERYGIFT is
 * still an ADD_PRODUCT offer rather than DISCOUNT_ON_PRODUCT. That extra line changes the cart
 * Shopflo evaluates — a ₹1,148 kit becomes a ₹1,297 cart — so it picks a different offer and
 * the shopper is charged more than this drawer promised. Not writing the line restores correct
 * pricing; the cost is that Shopflo asks for the gift again at checkout.
 *
 * Flip to true once Shopflo confirms DISCOUNT_ON_PRODUCT is live on this store.
 */
const GIFT_AS_CART_LINE = false;

const GIFT_CHOICE_KEY = 'tss_cart_gift_choice';

class CustomCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = 'custom-cart-drawer';
    this.routes = window.Shopify?.routes?.root ?? '/';
    this.pendingRemoval = null;
    this.lastFocused = null;
    this.strings = CustomCartDrawer.readStrings(this.dataset.ccStrings);
    this.celebratedHandles = CustomCartDrawer.readCelebrated();
    this.sheetTimer = null;
    this.sheetQueue = [];       // sheets waiting for the current one to close
    this.currentSheet = null;   // the sheet on screen, tracked synchronously
    this.sheetClosing = false;
    this.busyTimer = null;
    this.busyDepth = 0;
    this.giftDraft = new Map();  // variantId -> quantity, while the picker is open
    this.giftPromptedFor = null; // offer handle already offered this session
    this.giftPromptDue = false;  // prompt owed, opened after any celebration
    this.giftWriteInFlight = false; // one gift cart write at a time
    this.giftConfigWarnedFor = null; // offer handle already warned about
    this.tickerTimer = null;
    this.tickerIndex = 0;        // survives a re-render, so the ticker resumes in place
    this.lastProgress = null;    // last fill width shown, so the bar animates from it
    this.toastTimer = null;
    this.toastHideTimer = null;
    this.emptyingTimer = null;
    this.scrollLockY = 0;
    this.upsellAdded = new Set();

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCouponKeydown = this.handleCouponKeydown.bind(this);
    this.handleCouponInput = this.handleCouponInput.bind(this);
    this.handleOffersUpdated = this.handleOffersUpdated.bind(this);
    this.handleUpsellScroll = this.handleUpsellScroll.bind(this);

    this.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('theme:offers:updated', this.handleOffersUpdated);

    this.bindCouponInput();
    // The coupon panel lives outside the re-rendered markup, so it is bound once.
    this.bindCouponInput('[data-cc-coupons-input]', '[data-cc-coupons-apply]');
    this.paintSwaps();
    this.bindUpsell();
    this.paintUpsell();
    this.takeOverCartTriggers();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('theme:offers:updated', this.handleOffersUpdated);
    window.clearInterval(this.triggerTimer);
    window.clearInterval(this.tickerTimer);
    window.clearTimeout(this.sheetTimer);
    window.clearTimeout(this.busyTimer);
    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.toastHideTimer);
    window.clearTimeout(this.emptyingTimer);
  }

  /* ------------------------------------------------------------- triggers */

  /**
   * Points every cart trigger on the site at this drawer.
   *
   * Two openers are claimed, because the site has had two.
   *
   * `Alpine.store('xMiniCart').openCart` is the theme's own, and the important one: the
   * header icon, product pages, quick view, the routine kit, product cards and the quiz all
   * funnel through that single method, so replacing it diverts all of them at once.
   *
   * `window.handleFloCartBtn` is Shopflo's, kept for any trigger still calling it — the
   * layout patches that name too. `handleFloCheckoutBtn` is deliberately left alone, which is
   * what keeps Shopflo checkout working.
   *
   * The override replaces the method rather than calling through to it. The theme's openCart
   * also sets `xPopup.open`, which locks the body a second time on top of this drawer's own
   * scroll lock — the double lock is what used to leave the page stuck after closing.
   *
   * Re-asserted on a timer because neither owner is there at upgrade time: Alpine boots later
   * and may replace its stores, and Shopflo's bundle loads async and redefines its global.
   * The `isCustomCart` flag makes each claim idempotent, so re-running costs nothing.
   */
  takeOverCartTriggers() {
    const claim = () => {
      const open = () => {
        /*
         * The two things the theme's opener did that still matter. A quick view is a separate
         * modal, so left alone it stays on screen behind this drawer; and xPopup is the
         * theme's body lock, which has to come off or it survives this drawer closing and
         * leaves the page unscrollable. This drawer locks scrolling its own way.
         */
        const quickView = window.Alpine?.store?.('xQuickView');
        if (quickView?.show) quickView.show = false;
        window.Alpine?.store?.('xPopup')?.close?.();

        this.open();
      };
      open.isCustomCart = true;

      if (!window.handleFloCartBtn?.isCustomCart) window.handleFloCartBtn = open;

      const miniCart = window.Alpine?.store?.('xMiniCart');
      if (miniCart && !miniCart.openCart?.isCustomCart) miniCart.openCart = open;
    };

    claim();
    let ticks = 0;
    this.triggerTimer = window.setInterval(() => {
      claim();
      if (ticks++ > 100) window.clearInterval(this.triggerTimer);
    }, 200);
  }

  /* ------------------------------------------------------------ open/close */

  open() {
    if (this.classList.contains('is-open')) return;

    /*
     * Undo anything an emptied cart left behind. The class hides the empty state for the
     * closing slide, and without clearing it here a shopper who empties the cart and comes
     * back later finds it blank.
     *
     * The timer matters more: it is a close() already in flight. Reopening inside that beat
     * let it fire a moment later, shutting the drawer and releasing the scroll lock while
     * the shopper was still looking at it.
     */
    this.classList.remove('is-emptying');
    window.clearTimeout(this.emptyingTimer);

    this.lastFocused = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    // Opening without an interaction (?open-cart redirect) still has to pull in custom-cart.css
    document.dispatchEvent(new CustomEvent('theme:cart:opened'));
    this.lockScroll();
    this.querySelector('[data-cc-close]')?.focus();
    // Earliest point a celebration becomes plausible, so the animation is fetched now
    // rather than on every page load. Idle work; nothing waits on it.
    window.CustomCartConfetti?.preload(this);
    this.startTicker();
    this.refresh();
  }

  closeWhenEmptied() {
    this.classList.add('is-emptying');
    window.clearTimeout(this.emptyingTimer);
    this.emptyingTimer = window.setTimeout(
      () => this.close(),
      this.cssDuration('--cc-empty-close-delay', 140),
    );
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.closeSheet();
    this.closeCoupons();
    this.stopTicker();
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    this.lastFocused?.focus?.();
  }

  /* ------------------------------------------------------------- progress */

  /**
   * Animates the progress bar's fill. Everything else about the bar — the width, which
   * nodes are reached, the countdown copy — is computed in Liquid and re-rendered on every
   * cart change, so this deliberately recomputes nothing and cannot disagree with it.
   *
   * The one thing Liquid cannot do is animate: the panel's markup is replaced wholesale, so
   * the fill arriving is a brand new element that would simply appear at its new width. It
   * is put back where the last one finished, that is applied, and only then set to its real
   * value — which is what the CSS width transition needs in order to have somewhere to
   * travel from.
   *
   * The width is the sole inline style written from JavaScript anywhere in this file. A
   * percentage taken from cart data has no class-based equivalent, and it goes through a
   * custom property rather than `style.width` so the CSS rule still owns what that width
   * means.
   */
  paintProgress() {
    const fill = this.querySelector('[data-cc-progress-fill]');
    if (!fill) {
      // No bar on this render — an empty cart, or no active metaobject. Forget the last
      // width so the bar does not animate from a stale figure when it comes back.
      this.lastProgress = null;
      return;
    }

    const target = fill.dataset.fill;
    if (!target) return;

    if (this.lastProgress && this.lastProgress !== target) {
      fill.style.setProperty('--cc-progress-fill', this.lastProgress);
      this.flushStyles(fill);
    }

    fill.style.setProperty('--cc-progress-fill', target);
    this.lastProgress = target;
  }

  /**
   * A duration read out of CSS, in milliseconds. Accepts either unit — `2000ms` and `2s`
   * both mean the same thing — and falls back if the property is missing or unparseable.
   *
   * Shared by every timing the stylesheet owns, so a duration can never drift between the
   * transition that plays it and the timer that waits for it.
   */
  cssDuration(property, fallback) {
    const raw = window.getComputedStyle(this).getPropertyValue(property).trim();
    const value = Number.parseFloat(raw);
    if (!Number.isFinite(value)) return fallback;
    return raw.endsWith('ms') ? value : value * 1000;
  }

  /* ----------------------------------------------------------- scroll lock */

  /**
   * Holds the page still behind the open drawer.
   *
   * `overflow: hidden` on the body is not enough here — the theme sets no height on html or
   * body, so the document keeps scrolling regardless, and iOS ignores it outright. Pinning
   * the body with `position: fixed` at its current offset is what actually stops it, which
   * is the same technique the theme's own lock uses for the Shopflo drawer.
   *
   * Its own class rather than the theme's shared `overflow-hidden`: popup.js and the Alpine
   * bundle both remove that one for their own reasons, and either would silently release
   * the cart's lock while the drawer was still open.
   *
   * The offset goes through a custom property so the positioning stays in the stylesheet;
   * the only thing measured here is how far down the page already was.
   */
  lockScroll() {
    this.scrollLockY = window.scrollY || window.pageYOffset || 0;
    const root = document.documentElement;
    root.style.setProperty('--cc-scroll-y', `${this.scrollLockY}px`);
    root.classList.add('cc-scroll-locked');
  }

  unlockScroll() {
    const root = document.documentElement;
    if (!root.classList.contains('cc-scroll-locked')) return;

    root.classList.remove('cc-scroll-locked');
    root.style.removeProperty('--cc-scroll-y');
    // Pinning the body loses the scroll position, so it has to be put back by hand.
    window.scrollTo(0, this.scrollLockY ?? 0);
  }

  /* --------------------------------------------------------------- ticker */

  tickerInterval() {
    return this.cssDuration('--cc-ticker-interval', 2000);
  }

  /**
   * Idempotent: called on open and again after every re-render, since the panel's markup
   * is replaced wholesale and the previous lines go with it. `tickerIndex` lives on the
   * component rather than the DOM so a cart change does not send the ticker back to the
   * first offer mid-cycle.
   */
  startTicker() {
    this.stopTicker();

    // Nothing to animate behind a closed drawer, and a timer left running there would
    // never be the thing anyone notices was wrong.
    if (!this.classList.contains('is-open')) return;

    const items = Array.from(this.querySelectorAll('[data-cc-ticker-item]'));
    if (!items.length) return;

    if (this.tickerIndex >= items.length) this.tickerIndex = 0;
    this.showTickerItem(items, this.tickerIndex);

    // One line is a banner, not a ticker. Nothing to rotate to, so no timer.
    if (items.length < 2) return;

    this.tickerTimer = window.setInterval(() => {
      // Re-read rather than close over `items`: a re-render between ticks would leave the
      // captured nodes detached and the strip frozen on whatever was showing.
      const live = Array.from(this.querySelectorAll('[data-cc-ticker-item]'));
      if (live.length < 2) {
        this.stopTicker();
        return;
      }
      this.tickerIndex = (this.tickerIndex + 1) % live.length;
      this.showTickerItem(live, this.tickerIndex);
    }, this.tickerInterval());
  }

  stopTicker() {
    window.clearInterval(this.tickerTimer);
    this.tickerTimer = null;
  }

  /**
   * Makes the browser apply the classes set so far. Reading a layout property is the
   * standard way to force that flush. It is its own method so the sequencing in
   * showTickerItem is visible — and can be asserted in a test — rather than being an
   * unexplained property read that a tidy-up would delete.
   */
  flushStyles(element) {
    void element.offsetHeight;
  }

  /**
   * Moves `is-current` to one line and `is-leaving` to whichever line is handing over.
   * Both classes are needed: the outgoing line travels down and out, while the lines merely
   * waiting sit above. Sending the outgoing one straight back to the base state would read
   * as the ticker scrolling the wrong way.
   */
  showTickerItem(items, index) {
    const next = items[index];

    items.forEach((item) => {
      if (item === next) return;
      // Reading is-current before clearing it is what identifies the outgoing line.
      const leaving = item.classList.contains('is-current');
      item.classList.remove('is-current');
      item.classList.toggle('is-leaving', leaving);
    });

    if (!next) return;

    if (next.classList.contains('is-leaving')) {
      /*
       * This line left on the previous tick and is still parked below the strip. With only
       * two offers its turn comes round straight away, and promoting it from there animates
       * it upwards from below — the ticker visibly running backwards on every other change.
       *
       * Clearing the class is not enough on its own: both changes would land in the same
       * style recalculation and the browser would still start the transition from below.
       * It has to be put back in the waiting position above, and that has to be applied,
       * before it is promoted.
       */
      next.classList.remove('is-leaving');
      this.flushStyles(next);
    }

    next.classList.add('is-current');
  }

  /* --------------------------------------------------------------- events */

  handleClick(event) {
    const { target } = event;

    if (target.closest('[data-cc-close]') || target.closest('[data-cc-backdrop]')) {
      this.close();
      return;
    }

    const step = target.closest('[data-cc-qty]');
    if (step) {
      this.changeQuantity(step.closest('[data-cc-line]'), step.dataset.ccQty === 'up' ? 1 : -1);
      return;
    }

    if (target.closest('[data-cc-remove]')) {
      this.openRemoveSheet(target.closest('[data-cc-line]'));
      return;
    }

    if (target.closest('[data-cc-note-toggle]')) {
      this.toggleNote(this.querySelector('[data-cc-note-body]')?.hidden !== false);
      return;
    }

    if (target.closest('[data-cc-note-cancel]')) {
      this.cancelNote();
      return;
    }

    if (target.closest('[data-cc-note-save]')) {
      this.saveNote();
      return;
    }

    if (target.closest('[data-cc-note-delete]')) {
      this.deleteNote();
      return;
    }

    const upsellPage = target.closest('[data-cc-upsell-page]');
    if (upsellPage) {
      this.pageUpsell(upsellPage.dataset.ccUpsellPage);
      return;
    }

    const upsellAdd = target.closest('[data-cc-upsell-add]');
    if (upsellAdd) {
      this.addUpsell(upsellAdd);
      return;
    }

    const swapApply = target.closest('[data-cc-swap-apply]');
    if (swapApply) {
      this.applySwap(swapApply.closest('[data-cc-swap]'));
      return;
    }

    if (target.closest('[data-cc-swap-undo]')) {
      this.undoSwap(target.closest('[data-cc-swap-undo]'));
      return;
    }

    if (target.closest('[data-cc-sheet-close]')) {
      this.closeSheet();
      return;
    }

    if (target.closest('[data-cc-remove-confirm]')) {
      this.confirmRemoval();
      return;
    }

    const summaryToggle = target.closest('[data-cc-summary-toggle]');
    if (summaryToggle) {
      this.toggleDisclosure(summaryToggle, this.querySelector('[data-cc-breakdown]'));
      return;
    }

    const savingsToggle = target.closest('[data-cc-savings-toggle]');
    if (savingsToggle) {
      this.toggleDisclosure(savingsToggle, this.querySelector('[data-cc-savings-list]'));
      return;
    }

    const appliedToggle = target.closest('[data-cc-coupons-applied-toggle]');
    if (appliedToggle) {
      this.toggleDisclosure(appliedToggle, this.querySelector('[data-cc-coupons-applied-list]'));
      return;
    }

    const cartAppliedToggle = target.closest('[data-cc-cart-applied-toggle]');
    if (cartAppliedToggle) {
      this.toggleDisclosure(cartAppliedToggle, this.querySelector('[data-cc-cart-applied-list]'));
      return;
    }

    const couponToggle = target.closest('[data-cc-coupon-toggle]');
    if (couponToggle) {
      this.toggleDisclosure(
        couponToggle,
        couponToggle.closest('[data-cc-coupon]')?.querySelector('[data-cc-coupon-terms]'),
      );
      return;
    }

    if (target.closest('[data-cc-coupon-apply]')) {
      this.applyCoupon();
      return;
    }

    if (target.closest('[data-cc-coupons-apply]')) {
      this.applyCoupon('[data-cc-coupons-input]', '[data-cc-coupons-error]');
      return;
    }

    if (target.closest('[data-cc-more]') || target.closest('[data-cc-view-coupons]')) {
      this.openCoupons();
      return;
    }

    if (target.closest('[data-cc-coupons-back]')) {
      this.closeCoupons();
      return;
    }

    // Apply and remove are the same action wherever they appear — the inline row, the
    // savings breakdown, or a card in the coupon panel — so one pair of handlers
    // serves all three. The button carries the offer it belongs to.
    const offerApply = target.closest('[data-cc-offer-apply]');
    if (offerApply) {
      this.applyOffer(offerApply.dataset.offerHandle, offerApply.dataset.offerKind);
      return;
    }

    const offerRemove = target.closest('[data-cc-offer-remove]');
    if (offerRemove) {
      this.removeOffer(offerRemove.dataset.offerHandle);
      return;
    }

    if (target.closest('[data-cc-gift-start]')) {
      this.startGiftSelection();
      return;
    }

    const giftAdd = target.closest('[data-cc-gift-add]');
    if (giftAdd) {
      this.changeGiftDraft(giftAdd.closest('[data-cc-gift-option]'), 1);
      return;
    }

    const giftStep = target.closest('[data-cc-gift-step]');
    if (giftStep) {
      this.changeGiftDraft(
        giftStep.closest('[data-cc-gift-option]'),
        giftStep.dataset.ccGiftStep === 'up' ? 1 : -1,
      );
      return;
    }

    if (target.closest('[data-cc-gift-confirm]')) {
      this.confirmGiftSelection();
      return;
    }

    // "View" on an applied freebie, in the breakdown or the coupon panel. Same intent as
    // the pencil on the gift line: reopen the picker, no prompt in between.
    if (
      target.closest('[data-cc-gift-edit]')
      || target.closest('[data-cc-savings-view]')
      || target.closest('[data-cc-applied-row-view]')
    ) {
      this.openGiftSheet();
      return;
    }

    if (target.closest('[data-cc-checkout]')) {
      this.goToCheckout();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
    // Innermost layer first, so one press never closes more than the shopper meant.
    if (this.currentSheet) {
      this.closeSheet();
    } else if (this.couponsOpen) {
      this.closeCoupons();
    } else {
      this.close();
    }
  }

  /**
   * Both coupon inputs — the one in the cart and the one in the coupon panel — behave
   * identically, so they share these handlers. Each input names the button and error
   * line that belong to it, which is what keeps the two independent.
   */
  bindCouponInput(inputSelector = '[data-cc-coupon-input]', applySelector = '[data-cc-coupon-apply]') {
    const input = this.querySelector(inputSelector);
    if (!input) return;
    input.dataset.ccApplyTarget = applySelector;
    input.addEventListener('keydown', this.handleCouponKeydown);
    input.addEventListener('input', this.handleCouponInput);
  }

  handleCouponKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (event.target.matches('[data-cc-coupons-input]')) {
      this.applyCoupon('[data-cc-coupons-input]', '[data-cc-coupons-error]');
    } else {
      this.applyCoupon();
    }
  }

  /** Arrow visibility follows the track's position, so it is recomputed as it moves. */
  handleUpsellScroll() {
    this.paintUpsell();
  }

  handleCouponInput(event) {
    const apply = this.querySelector(event.target.dataset.ccApplyTarget);
    if (apply) apply.hidden = event.target.value.trim() === '';
  }

  /**
   * A panel marked `data-cc-collapse` animates open and shut, so it has to stay in the
   * layout and be driven by a class — `hidden` removes it from the box tree, leaving a
   * transition nothing to run from. Every other disclosure is a plain show and hide.
   */
  toggleDisclosure(trigger, panel) {
    if (!panel) return;
    const open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!open));

    if (panel.hasAttribute('data-cc-collapse')) {
      panel.classList.toggle('is-open', !open);
    } else {
      panel.hidden = open;
    }
  }

  /* ------------------------------------------------------ cart mutations */

  async changeQuantity(line, delta) {
    if (!line) return;
    const next = Number(line.querySelector('[data-cc-qty-value]').textContent) + delta;

    /*
     * Minus is disabled at one, so reaching zero this way means something got past the
     * markup — a stale button after a repaint, or a keyboard activation on a disabled
     * control. Removing is the bin's job, so this does nothing rather than deleting a line
     * the shopper only meant to decrement.
     */
    if (next < 1) return;

    line.querySelector('[data-cc-stepper]')?.classList.add('is-busy');
    await this.mutate({ id: line.dataset.lineKey, quantity: next });
  }

  openRemoveSheet(line) {
    if (!line) return;
    this.pendingRemoval = line.dataset.lineKey;

    const sheet = this.querySelector('[data-cc-sheet="remove"]');
    const preview = this.querySelector('[data-cc-remove-preview]');
    const template = this.querySelector('[data-cc-remove-template]');
    if (!sheet || !preview || !template) return;

    const quantity = line.querySelector('[data-cc-qty-value]').textContent.trim();
    const title = this.querySelector('[data-cc-remove-title]');
    if (title) {
      title.textContent = quantity === '1'
        ? this.t('removeConfirmOne')
        : this.t('removeConfirmOther', { count: quantity });
    }

    /*
     * Removing the only line empties the cart, so the button says where that leaves the
     * shopper. Counted in lines rather than units: one line holding three of something
     * still empties the cart, and the gift line does not count — it carries no
     * data-cc-line, and it goes when the paid items do.
     */
    const isLastLine = this.querySelectorAll('[data-cc-line]').length <= 1;
    const label = this.querySelector('[data-cc-remove-confirm-label]');
    const lastLabel = this.querySelector('[data-cc-remove-confirm-last]');
    if (label) label.hidden = isLastLine;
    if (lastLabel) lastLabel.hidden = !isLastLine;

    // Clone the Liquid template rather than building markup in JavaScript.
    const clone = template.content.cloneNode(true);
    const sourceImage = line.querySelector('.custom-cart-line__media img');
    const cloneImage = clone.querySelector('img');
    if (sourceImage && cloneImage) {
      cloneImage.src = sourceImage.src;
      cloneImage.alt = sourceImage.alt;
    }
    clone.querySelector('[data-cc-preview-title]').textContent =
      line.querySelector('.custom-cart-line__title').textContent.trim();

    const variant = line.querySelector('.custom-cart-line__variant');
    const previewVariant = clone.querySelector('[data-cc-preview-variant]');
    if (variant && previewVariant) {
      previewVariant.textContent = variant.textContent.trim();
    } else {
      previewVariant?.remove();
    }

    clone.querySelector('[data-cc-preview-price]').textContent =
      line.querySelector('[data-cc-line-price]').textContent.trim();

    // Display only — the sheet's stepper has no handlers behind it.
    const previewQuantity = clone.querySelector('[data-cc-preview-qty]');
    if (previewQuantity) previewQuantity.textContent = quantity;

    const was = line.querySelector('[data-cc-line-was]');
    const previewWas = clone.querySelector('[data-cc-preview-was]');
    if (was && !was.hidden) {
      previewWas.textContent = was.textContent.trim();
      previewWas.hidden = false;
    }

    preview.replaceChildren(clone);
    this.openSheet(sheet);
  }

  /**
   * Opens a sheet. One place for it so every sheet animates in the same way and, when
   * it closes itself, always shows the countdown on its confirm button.
   *
   * `autoClose` sheets are marked `is-timed`, which is what starts that countdown. The
   * length is read from CSS rather than set here, so the bar and this timer cannot
   * drift apart.
   */
  openSheet(sheet, { autoClose = false, onOpen = null } = {}) {
    if (!sheet) return;

    /*
     * One sheet at a time, always. Two of them can fall due on the same repaint — a
     * freebie unlocking while a discount is being celebrated, for instance — and
     * stacking them puts two dialogs on screen with the one behind still interactive.
     * The second waits its turn instead, and closeSheet starts it.
     *
     * The check is against a held reference, NOT against `.is-open` in the DOM. That
     * class is added a frame later so the sheet has something to animate from, and two
     * openSheet calls in the same tick — which is exactly how the celebration and the
     * freebie prompt arrive — would both look out, see no open sheet, and both open.
     *
     * Note for anything added here later: these elements are outside the cart panel and
     * so live for as long as the page does. Any state put on a sheet has to be taken off
     * again — the panel re-render no longer does it for you.
     */
    if (this.currentSheet || this.sheetClosing) {
      if (this.currentSheet !== sheet && !this.sheetQueue.some((entry) => entry.sheet === sheet)) {
        this.sheetQueue.push({ sheet, autoClose, onOpen });
      }
      return;
    }

    window.clearTimeout(this.sheetTimer);
    this.currentSheet = sheet;
    sheet.classList.toggle('is-timed', autoClose);
    sheet.hidden = false;
    // Next frame, so the transition runs from the closed position. Checked again on the
    // way in: a sheet closed within the same tick must not be reopened by its own frame.
    requestAnimationFrame(() => {
      if (this.currentSheet === sheet) sheet.classList.add('is-open');
    });
    onOpen?.();

    if (!autoClose) return;
    this.sheetTimer = window.setTimeout(() => this.closeSheet(), this.sheetTimeout());
  }

  /** The self-close delay, taken from --cc-sheet-timeout so CSS stays the authority. */
  sheetTimeout() {
    return this.cssDuration('--cc-sheet-timeout', 2800);
  }

  /** Resolves once the sheet is out of the way, so one sheet can hand over to another. */
  closeSheet() {
    window.clearTimeout(this.sheetTimer);
    const sheet = this.currentSheet;
    if (!sheet) return Promise.resolve();

    this.currentSheet = null;
    sheet.classList.remove('is-open');
    // Still occupies the slot until it has finished sliding away, so the next sheet
    // does not appear over the top of one that is still on screen.
    this.sheetClosing = true;
    this.pendingRemoval = null;

    return new Promise((resolve) => window.setTimeout(() => {
      sheet.hidden = true;
      // Cleared so the countdown starts from empty the next time this sheet opens.
      sheet.classList.remove('is-timed');
      this.sheetClosing = false;

      // Anything that fell due while this one was up gets its turn now.
      const next = this.sheetQueue.shift();
      if (next) this.openSheet(next.sheet, { autoClose: next.autoClose, onOpen: next.onOpen });

      resolve();
    }, 260));
  }

  /**
   * The confirm button is deliberately NOT marked busy here. The sheet starts closing in
   * the same breath, so the state would show for the length of a slide-out and nothing
   * ever took it off again — and since sheets now live outside the cart panel, they are
   * no longer wiped by the re-render that used to clear it by accident. The button came
   * back disabled the next time the sheet opened.
   *
   * Waiting is reported by the drawer instead: the skeleton and the dimmed summary.
   */
  async confirmRemoval() {
    if (!this.pendingRemoval) return;
    const key = this.pendingRemoval;

    this.querySelector(`[data-line-key="${key}"]`)?.classList.add('is-leaving');
    this.closeSheet();
    // Let the collapse finish so the row does not vanish abruptly.
    await new Promise((resolve) => window.setTimeout(resolve, 180));
    await this.mutate({ id: key, quantity: 0 });
  }

  /* ------------------------------------------------------------ busy state */

  /**
   * Marks the cart as recalculating, which puts a skeleton where the offers card is and
   * takes the summary out of reach — the totals on it are stale until the round trip
   * lands, and checking out against a stale total would be worse than a short wait.
   *
   * Counted rather than boolean: a cart write calls refresh(), which is itself a busy
   * operation, and the inner one finishing must not clear the outer one's state.
   *
   * Held back for 180ms before anything appears. On a quick connection the work is done
   * in less time than it takes to register a skeleton, and one that flashes in and out
   * reads worse than a brief pause.
   */
  setBusy(busy) {
    this.busyDepth = Math.max((this.busyDepth ?? 0) + (busy ? 1 : -1), 0);

    if (this.busyDepth > 0) {
      if (this.busyTimer || this.classList.contains('is-loading')) return;
      this.setAttribute('aria-busy', 'true');
      this.busyTimer = window.setTimeout(() => {
        this.busyTimer = null;
        this.classList.add('is-loading');
      }, 180);
      return;
    }

    window.clearTimeout(this.busyTimer);
    this.busyTimer = null;
    this.classList.remove('is-loading');
    this.removeAttribute('aria-busy');
  }

  /** Runs `work` with the cart marked busy, however it ends. */
  async whileBusy(work) {
    this.setBusy(true);
    try {
      return await work();
    } finally {
      this.setBusy(false);
    }
  }

  /** Change one line. */
  mutate(body) {
    return this.postCart('cart/change.js', body);
  }

  /**
   * Change several lines at once. A swap removes one line and adds another, and both
   * have to land in the same request — two calls would leave the cart briefly holding
   * neither, which the offer engine would price and the shopper would see.
   */
  mutateUpdates(updates) {
    return this.postCart('cart/update.js', { updates });
  }

  /**
   * Add to the cart. Separate from mutateUpdates because update.js sets an ABSOLUTE
   * quantity: `{ [variantId]: 1 }` on a line already holding three would cut it to one.
   * add.js is always additive, which is what an Add button means.
   */
  mutateAdd(variantId, quantity = 1) {
    return this.postCart('cart/add.js', { items: [{ id: variantId, quantity }] });
  }

  themeSections() {
    return window.Alpine?.store?.('xCartHelper')?.getSectionsToRender?.() ?? [];
  }

  applyThemeSections(sections, rendered) {
    if (!rendered) return;

    for (const section of sections) {
      const target = document.querySelector(section.selector);
      const markup = rendered[section.id];
      if (!target || !markup) continue;

      // The response holds a whole section; only the matching element's contents are wanted.
      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector(section.selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    }
  }

  /**
   * How many gifts are on screen but not in the cart.
   *
   * Only the merchant-picked case: Shopflo adds that product during checkout, so Shopify's
   * cart — and therefore Liquid's count — knows nothing about it. A shopper-picked gift is a
   * real line and is already counted, so this returns 0 for it and the two never double up.
   *
   * Taken from the offer rather than the rendered rows, because the count is painted in the
   * same pass that draws them and a refresh has just emptied the panel.
   */
  giftDisplayCount() {
    const offer = window.CartOffers?.eligibleGiftOffer();
    if (!offer || this.writesGiftLine(offer)) return 0;

    return this.giftDisplayItems(offer).reduce((sum, { quantity }) => sum + quantity, 0);
  }

  /**
   * The number the shopper is shown: everything they are walking away with.
   *
   * data-item-count is Liquid's figure — paid items plus any gift that really is a cart
   * line — and the display-only gifts are added on top.
   */
  shownCount() {
    return (Number(this.dataset.itemCount) || 0) + this.giftDisplayCount();
  }

  /**
   * Puts that number everywhere it appears: the drawer's own heading, the header bubbles, and
   * Alpine, which several parts of the theme read it from.
   *
   * The bubbles have to be written rather than left to the section render, because the section
   * renders from Shopify's cart.item_count and a merchant-picked gift is not in the cart. On a
   * shopper-picked gift the two figures already agree, so this writes the same value the
   * server would have.
   */
  syncThemeCount() {
    const count = this.shownCount();

    const helper = window.Alpine?.store?.('xCartHelper');
    if (helper) helper.currentItemCount = count;

    this.setText('[data-cc-count]', `(${count})`);

    for (const bubble of document.querySelectorAll('#cart-icon-bubble span, #mobile-cart-icon-bubble span')) {
      bubble.textContent = count > 99 ? '99+' : String(count);
      // The coloured disc is the span's parent, and it is what hides on an empty cart.
      bubble.parentElement?.classList.toggle('hidden', count === 0);
    }
  }

  /**
   * A cart URL with Shopflo's auto-open suppressed.
   *
   * Shopflo's bundle wraps both fetch and XMLHttpRequest, and calls `window.handleFloCartBtn()`
   * on any request whose path ends in /cart/add.js. That global is this drawer's opener, so
   * every add would pop the drawer open — including the gift lines written on page load, which
   * the shopper never asked to see. `skip-cart-open=true` is the bundle's own opt-out.
   *
   * Only our own writes carry it. The theme's add-to-cart calls are left as they are, so a
   * product-page add still opens this drawer, which is what it should do.
   */
  cartUrl(route) {
    return `${this.routes}${route}${route.includes('?') ? '&' : '?'}skip-cart-open=true`;
  }

  /** One place for every cart write, so refresh and error handling stay consistent. */
  async postCart(route, body) {
    this.setBusy(true);
    try {
      const sections = this.themeSections();

      /*
       * This drawer's own section is requested alongside the theme's, so one request returns
       * the mutation, the theme's cart sections AND our repainted markup.
       *
       * It used to take four round trips to change one line: the mutation, a separate
       * ?section_id= render of this drawer, and /cart.js twice — once from the recompute inside
       * refresh() and again from the debounced one our own items-changed dispatch scheduled.
       * Three of those asked the server for something this response already contains.
       */
      /*
       * Shopify renders at most five sections per request, and the theme's list can already be
       * five — on the cart page it is. A sixth would fail the whole request and take the
       * mutation with it, so ours is appended only when there is room.
       *
       * Deduped first, because the theme asks for `quick-order-list` twice and a wasted slot is
       * the difference between fitting and not. Only the request is deduped; applyThemeSections
       * still walks the full list, so both quick-order-list targets are painted from the one
       * rendered copy.
       *
       * When there is no room the response simply carries no section of ours and the fallback
       * below fetches it separately — the old behaviour, not a failure.
       */
      const themeIds = [...new Set(sections.map((section) => section.id))];
      const requested = themeIds.includes(this.sectionId) || themeIds.length >= 5
        ? themeIds
        : [...themeIds, this.sectionId];

      const response = await fetch(this.cartUrl(route), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...body,
          sections: requested,
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(`${route} responded ${response.status}`);
      const parsed = await response.json();

      this.applyThemeSections(sections, parsed.sections);

      // Let the rest of the theme — cart bubble, upsells — react as it always has. The
      // recompute this schedules is cancelled by the one below, which has better data.
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

      /*
       * `parsed` is the cart, so the engine needs no fetch of its own. If the section came back
       * empty for any reason, fall back to fetching both rather than leaving the drawer stale.
       */
      const own = parsed.sections?.[this.sectionId];
      if (!own || !(await this.applyRefresh(own, parsed))) await this.refresh();

      this.syncThemeCount();

      if (Number(this.dataset.paidCount) === 0) this.closeWhenEmptied();
    } catch (error) {
      console.error('[custom-cart] cart update failed:', error);
      await this.refresh();
    } finally {
      this.setBusy(false);
    }
  }

  /* -------------------------------------------------------------- refresh */

  /**
   * Re-render the section, then recompute offers and patch the result in. Doing
   * both in sequence avoids a flash of undiscounted prices.
   */
  /**
   * Fetch this section's markup and repaint from it.
   *
   * Only for callers that have no fresher markup to hand. Every cart/*.js response can carry
   * this section already — see postCart — so the fast path never comes through here.
   */
  async refresh() {
    this.setBusy(true);
    try {
      const response = await fetch(`${this.routes}?section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(`section render responded ${response.status}`);
      if (!await this.applyRefresh(await response.text())) {
        throw new Error('section render carried no custom-cart-drawer element');
      }
    } catch (error) {
      console.error('[custom-cart] refresh failed:', error);
    } finally {
      this.setBusy(false);
    }
  }

  /**
   * Repaint the drawer from already-rendered markup.
   *
   * `cartJson` is the cart from whichever request produced that markup. Passing it lets the
   * offer engine evaluate without a further /cart.js of its own; without it, it fetches.
   *
   * Returns whether it applied. Markup without our element in it means nothing was painted, and
   * a caller with another way to get the section can take it rather than leave a stale drawer.
   * Nothing is touched before that check, so false always means "no change made".
   */
  async applyRefresh(markup, cartJson) {
    const fresh = new DOMParser().parseFromString(markup, 'text/html')
      .querySelector('custom-cart-drawer');
    if (!fresh) return false;

    for (const attribute of [
      'data-item-count', 'data-paid-count', 'data-selling-total', 'data-mrp-total', 'data-gift-total',
    ]) {
      this.setAttribute(attribute, fresh.getAttribute(attribute) ?? '0');
    }

    const panel = this.querySelector('.custom-cart__panel');
    const freshPanel = fresh.querySelector('.custom-cart__panel');
    if (panel && freshPanel) panel.innerHTML = freshPanel.innerHTML;

    this.bindCouponInput();
    // Not inside applyOfferResult: swap state is independent of offers, and the swap
    // controls must still be right if the offer engine fails to load.
    this.paintSwaps();
    this.bindUpsell();
    this.reorderUpsell();
    this.paintUpsell();
    this.paintProgress();
    // The ticker's lines were just replaced along with the rest of the panel.
    this.startTicker();

    const result = cartJson
      ? await window.CartOffers?.recomputeWith(cartJson)
      : await window.CartOffers?.recompute();
    if (result) this.applyOfferResult(result);

    return true;
  }

  handleOffersUpdated(event) {
    // Fires for cart changes made anywhere else on the site, e.g. a PDP add.
    if (!this.classList.contains('is-open')) {
      this.syncGiftWhileClosed();
      return;
    }
    this.applyOfferResult(event.detail.result);
  }

  syncGiftWhileClosed() {
    const cart = window.CartOffers?.rawCart;
    if (!cart) return;

    const held = (cart.items ?? []).filter((item) => item.properties?._gift);
    if (!held.length) return;

    const offer = window.CartOffers?.eligibleGiftOffer() ?? null;

    /*
     * A gift line only ever belongs to the offer that put it there, and only while that offer
     * is live and picked by the shopper. Anything else is a line Shopflo will not discount, so
     * the shopper would be charged for something they were told was free — and they can reach
     * checkout from the header without ever opening this drawer.
     *
     * Three ways to end up here: the cart dropped below the threshold, the offer was switched
     * off, or the offer was changed to "We choose the gift", where the gift must NOT be a cart
     * line because Shopflo adds it itself.
     */
    const keep = offer
      && this.writesGiftLine(offer)
      && held.every((line) => line.properties._gift === offer.handle);

    if (!keep) this.writeGiftLines(null, []);
  }

  /* --------------------------------------------------- render engine result */

  applyOfferResult(result) {
    this.classList.add('is-repricing');

    const sellingTotal = Number(this.dataset.sellingTotal || 0);
    const mrpTotal = Number(this.dataset.mrpTotal || 0);
    const discount = result.totalDiscount;
    const grandTotal = sellingTotal - discount;

    // Computed once and shared: listing an offer costs an engine run, so the cart row
    // and the coupon panel must not each ask for their own copy.
    const listing = this.listOffers();

    this.paintLines(result);
    this.paintOffers(result, listing);
    this.paintSummary({ sellingTotal, mrpTotal, discount, grandTotal });
    if (this.couponsOpen) this.paintCoupons(result, listing);

    window.requestAnimationFrame(() => this.classList.remove('is-repricing'));
    /*
     * Two separate orderings here, and conflating them is what made this awkward.
     *
     * COMPUTE order: syncGift settles gift state before anything is announced, so a gift
     * that has just lapsed is never celebrated.
     *
     * DISPLAY order: the freebie prompt goes up first and the celebration queues behind
     * it, matching Shopflo. The discount is already on the cart either way; the freebie
     * is the one that still needs the shopper to do something, so it should not be stuck
     * behind an animation. Whichever is due alone simply opens straight away.
     */
    this.syncGift();

    /*
     * After syncGift, because whether a gift is display-only or a cart line is what the count
     * depends on. Here rather than only after our own cart writes, so the number is also right
     * on first open and when the cart was changed somewhere else on the site.
     */
    this.syncThemeCount();

    if (this.giftPromptDue) {
      this.giftPromptDue = false;
      this.promptForGift();
    }

    this.celebrateNewOffers(result, listing);
  }

  /** What is applied and what could be, as the coupon layer describes it. */
  listOffers() {
    return window.CartOffers?.listOffers({
      // Either representation means the shopper is getting the gift, so the offer reads as
      // applied — a merchant-picked one has no cart line to look for.
      chosenGiftHandle: this.querySelector('[data-cc-gift-line], [data-cc-gift-display]')
        ?.dataset.giftOffer ?? null,
    }) ?? { applied: [], available: [] };
  }

  paintLines(result) {
    const rows = [...this.querySelectorAll('[data-cc-line]')].map((line) => {
      const entry = result.lineAdjustments.find((item) => item.lineKey === line.dataset.lineKey);
      const original = Number(line.dataset.lineOriginal || 0);
      return { line, original, lineDiscount: entry?.discount ?? 0, after: entry?.adjustedTotal ?? original };
    });

    // A cart-level offer discounts the whole cart, so it has no lineAdjustment of its own.
    // Spread it over what each line still costs after line offers — the base the engine charged
    // it against — so the lines keep adding up to the total.
    const cartDiscount = (result.cartAdjustments ?? []).reduce((sum, { amount }) => sum + amount, 0);
    const base = rows.reduce((sum, row) => sum + row.after, 0);
    let spread = 0;
    rows.forEach((row, index) => {
      // Residue on the last line, so paise lost to flooring cannot make the lines disagree.
      row.share = cartDiscount <= 0 || base <= 0
        ? 0
        : index === rows.length - 1
          ? cartDiscount - spread
          : Math.floor((cartDiscount * row.after) / base);
      spread += row.share;
    });

    for (const { line, original, lineDiscount, after, share } of rows) {
      const mrp = Number(line.dataset.lineMrp || 0);
      const discount = lineDiscount + share;
      const final = after - share;

      // The struck price is the highest "before" figure we can honestly show:
      // the compare-at total when there is one, otherwise the pre-offer price.
      const was = Math.max(mrp, discount > 0 ? original : 0);

      // A line discounted to nothing reads FREE! rather than Rs 0, like a free gift.
      const isFree = final <= 0;

      const priceEl = line.querySelector('[data-cc-line-price]');
      const wasEl = line.querySelector('[data-cc-line-was]');
      if (priceEl) {
        priceEl.textContent = isFree ? this.t('free') : this.money(final);
        // Toggled, not just added: the line stops being free the moment the offer lapses.
        priceEl.classList.toggle('is-free', isFree);
      }
      if (wasEl) {
        wasEl.hidden = was <= final;
        wasEl.textContent = was > final ? this.money(was) : '';
      }
    }
  }

  /**
   * The offer area of the cart. One headline at most, chosen in this order:
   *
   *   one offer discounting the cart  → that offer, with Remove
   *   several                         → collapsible breakdown, each row removable
   *   none, but one is worth having   → the best one, with Apply
   *   none, but coupons exist         → "View Coupons"
   *   nothing to show at all          → the coupon field, and only then
   *
   * The coupon field is the last resort rather than a permanent fixture. Whenever the
   * drawer has something in it the field lives there instead, so a shopper is never
   * offered two places to type the same code.
   *
   * A free gift is applied but discounts nothing, so it never occupies the headline row;
   * it shows as its own cart line and as a card in the coupon drawer.
   */
  paintOffers(result, listing) {
    const paying = listing.applied.filter((offer) => offer.amount > 0);
    const single = paying.length === 1 ? paying[0] : null;
    const invite = paying.length === 0
      ? listing.available.find((offer) => offer.amount > 0) ?? null
      : null;

    const hasApplied = listing.applied.length > 0;
    const count = listing.available.length;
    // Anything for the drawer to show: an offer to apply, or one to remove.
    const hasDrawer = count > 0 || hasApplied;

    /*
     * Everything applicable is on the cart. The offers card has nothing left to do — no code
     * to enter, no offer to apply, and a "More offers" row that would open an empty drawer —
     * so it gives up its slot to a plain statement of what was saved.
     *
     * Removing a row from that block puts an offer back within reach, count goes above zero,
     * and the card returns on the next repaint. That is the whole of the way back.
     */
    const allApplied = hasApplied && count === 0;

    this.paintAppliedList(result, listing.applied, {
      box: '[data-cc-cart-applied]',
      label: '[data-cc-cart-applied-label]',
      list: '[data-cc-cart-applied-list]',
      visible: allApplied,
    });

    this.paintOfferRow(result, single, invite);
    // Everything applied, not just what discounts money: a freebie belongs in the
    // breakdown too, and it is the only thing to report when it is applied on its own.
    this.paintSavingsList(result, listing.applied, single);

    const entry = this.querySelector('[data-cc-coupon-entry]');
    if (entry) entry.hidden = hasDrawer;

    /*
     * The card is bordered, so an empty one shows as a stray rounded rectangle. It used to
     * be impossible to empty: the coupon field was the last resort and always had something
     * in it. With coupon entry off there is no field, so a cart no offer applies to leaves
     * every child hidden and the card has to go too.
     */
    const card = this.querySelector('[data-cc-offers]');
    if (card) card.hidden = allApplied || (!hasDrawer && !entry);
    // A stale "code not valid" line must not survive the field being taken away.
    const error = this.querySelector('[data-cc-coupon-error]');
    if (error && hasDrawer) error.hidden = true;

    const view = this.querySelector('[data-cc-view-coupons]');
    if (view) view.hidden = hasApplied || Boolean(invite) || !hasDrawer;

    const more = this.querySelector('[data-cc-more]');
    if (more) {
      more.hidden = !hasDrawer;
      this.setText('[data-cc-more-label]', count > 0
        ? this.t('moreOffersCount', { count })
        : this.t('moreOffers'));

      // With nothing applied the inline field is gone, so the drawer is where a code
      // gets entered and the call to action says that rather than "view".
      //
      // Unless coupon entry is off, in which case the "enter" wording is not rendered at
      // all and "view" has to stay put — swapping to a label that does not exist would
      // leave the button with no text under it.
      const viewCta = this.querySelector('[data-cc-more-cta-view]');
      const enterCta = this.querySelector('[data-cc-more-cta-enter]');
      if (viewCta) viewCta.hidden = Boolean(enterCta) && !hasApplied;
      if (enterCta) enterCta.hidden = hasApplied;
    }
  }

  paintOfferRow(result, single, invite) {
    const row = this.querySelector('[data-cc-offer-row]');
    if (!row) return;

    const shown = single ?? invite;
    row.hidden = !shown;
    row.classList.toggle('is-applied', Boolean(single));

    const tick = row.querySelector('[data-cc-offer-tick]');
    const remove = row.querySelector('[data-cc-offer-remove]');
    const apply = row.querySelector('[data-cc-offer-apply]');

    if (tick) tick.hidden = !single;
    if (remove) remove.hidden = !single;
    if (apply) apply.hidden = Boolean(single) || !invite;
    if (!shown) return;

    row.querySelector('[data-cc-offer-amount]').textContent = single
      // With one offer in play its amount is the cart's whole discount, and the total
      // is the figure the shopper can check against the summary.
      ? this.t('savingsAmount', { amount: this.money(result.totalDiscount) })
      : this.t('saveAmount', { amount: this.money(invite.amount) });
    row.querySelector('[data-cc-offer-label]').textContent =
      this.t('savingsWithOffer', { name: shown.name });

    const button = single ? remove : apply;
    if (button) {
      button.dataset.offerHandle = shown.handle;
      button.dataset.offerKind = shown.kind;
      button.setAttribute(
        'aria-label',
        this.t(single ? 'removeOffer' : 'applyOffer', { name: shown.name }),
      );
    }
  }

  /**
   * The collapsible breakdown of everything applied.
   *
   * Shown whenever the flat row above is not already saying it all — so for two or more
   * applied offers, and also for a lone freebie, which has no amount for that row to
   * carry but still needs reporting.
   */
  paintSavingsList(result, applied, single) {
    const toggle = this.querySelector('[data-cc-savings-toggle]');
    const list = this.querySelector('[data-cc-savings-list]');
    if (!toggle || !list) return;

    const show = applied.length > 0 && !single;
    toggle.hidden = !show;
    list.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (!show) return;

    this.setText('[data-cc-savings-label]',
      this.t('savedWithDiscounts', { amount: this.savedLabel(result) }));

    // Rows are cloned from a Liquid template — markup never originates in JS.
    const template = this.querySelector('[data-cc-savings-row-template]');
    list.replaceChildren(...applied.map((offer) => {
      const row = template.content.cloneNode(true);
      row.querySelector('[data-cc-savings-name]').textContent = offer.name;
      this.paintAppliedRowEnding(row, offer, {
        amount: '[data-cc-savings-amount]',
        view: '[data-cc-savings-view]',
      });
      return row;
    }));
  }

  /**
   * What the heading reports. A freebie applied on its own discounts nothing, and
   * "₹0 Saved with discounts!" would read as a fault — so it says FREE! instead.
   */
  savedLabel(result) {
    return result.totalDiscount > 0 ? this.money(result.totalDiscount) : this.t('free');
  }

  /**
   * The right-hand end of an applied row: a cross always, preceded by an amount for a
   * discount or View for a freebie the shopper picks from several. Shared by the inline
   * breakdown and the coupon panel so the two cannot drift apart.
   */
  paintAppliedRowEnding(row, offer, selectors) {
    const isGift = offer.kind === 'gift';

    const amount = row.querySelector(selectors.amount);
    if (amount) {
      amount.hidden = isGift;
      amount.textContent = isGift ? '' : `- ${this.money(offer.amount)}`;
    }

    const canView = isGift && offer.shopperChosen && offer.giftCount > 1;
    const view = row.querySelector(selectors.view);
    if (view) {
      view.hidden = !canView;
      if (canView) view.setAttribute('aria-label', this.t('viewOffer', { name: offer.name }));
    }

    // Gifts are removable too. Skip at the prompt only closes the sheet, and paintOfferRow
    // already offers the cross when a gift is the only applied offer — hiding it here left a
    // gift undeclinable as soon as a second offer applied.
    const remove = row.querySelector('[data-cc-offer-remove]');
    if (remove) {
      remove.hidden = false;
      this.tagOfferButton(remove, offer, 'removeOffer');
    }
  }

  /** Points a cloned Apply or Remove button at the offer it belongs to. */
  tagOfferButton(button, offer, labelKey) {
    if (!button) return;
    button.dataset.offerHandle = offer.handle;
    button.dataset.offerKind = offer.kind;
    button.setAttribute('aria-label', this.t(labelKey, { name: offer.name }));
  }

  /* -------------------------------------------------------- coupons panel */

  get couponsOpen() {
    return Boolean(this.querySelector('[data-cc-coupons].is-open'));
  }

  openCoupons() {
    const panel = this.querySelector('[data-cc-coupons]');
    if (!panel || this.couponsOpen) return;

    this.paintCoupons(window.CartOffers?.result, this.listOffers());
    panel.hidden = false;
    // Next frame, so the slide runs from off-screen rather than snapping into place.
    requestAnimationFrame(() => panel.classList.add('is-open'));
    panel.querySelector('[data-cc-coupons-back]')?.focus();
  }

  /**
   * Resolves once the panel is out of the way. Applying an offer waits for that,
   * because the celebration sheet is painted inside the cart panel and would
   * otherwise appear behind this one.
   */
  closeCoupons() {
    const panel = this.querySelector('[data-cc-coupons]');
    if (!panel || panel.hidden) return Promise.resolve();

    panel.classList.remove('is-open');
    this.querySelector('[data-cc-coupons-error]')?.setAttribute('hidden', '');
    return new Promise((resolve) => window.setTimeout(() => {
      panel.hidden = true;
      // Looked up now rather than earlier: the cart behind may have re-rendered.
      this.querySelector('[data-cc-more]')?.focus();
      resolve();
    }, 300));
  }

  paintCoupons(result, listing) {
    const panel = this.querySelector('[data-cc-coupons]');
    if (!panel || !result) return;

    this.setText('[data-cc-coupons-value]', this.money(result.baseSubtotal));
    this.paintAppliedCoupons(result, listing.applied);

    const list = this.querySelector('[data-cc-coupons-list]');
    const template = this.querySelector('[data-cc-coupon-card-template]');
    if (list && template) {
      list.replaceChildren(...listing.available.map((offer) => this.buildCouponCard(template, offer)));
    }

    const label = this.querySelector('[data-cc-coupons-label]');
    if (label) label.hidden = listing.available.length === 0;

    const empty = this.querySelector('[data-cc-coupons-empty]');
    if (empty) empty.hidden = listing.available.length > 0 || listing.applied.length > 0;
  }

  /**
   * Fills an "applied discounts" block. There are two of them — one in the coupon drawer,
   * one standing in for the offers card in the cart — and they share this and the row
   * template, so the two can never disagree about what is applied.
   *
   * `visible` is separate from whether anything is applied, because the cart's copy also
   * depends on there being nothing left to apply.
   */
  paintAppliedList(result, applied, { box, label, list, visible }) {
    const boxEl = this.querySelector(box);
    const listEl = this.querySelector(list);
    if (!boxEl || !listEl) return;

    boxEl.hidden = !visible;
    if (!visible) return;

    this.setText(label, this.t('savedWithDiscounts', { amount: this.savedLabel(result) }));

    const template = this.querySelector('[data-cc-applied-row-template]');
    listEl.replaceChildren(...applied.map((offer) => {
      const row = template.content.cloneNode(true);
      row.querySelector('[data-cc-applied-row-name]').textContent = offer.name;
      this.paintAppliedRowEnding(row, offer, {
        amount: '[data-cc-applied-row-amount]',
        view: '[data-cc-applied-row-view]',
      });
      return row;
    }));
  }

  paintAppliedCoupons(result, applied) {
    this.paintAppliedList(result, applied, {
      box: '[data-cc-coupons-applied]',
      label: '[data-cc-coupons-applied-label]',
      list: '[data-cc-coupons-applied-list]',
      visible: applied.length > 0,
    });
  }

  buildCouponCard(template, offer) {
    const card = template.content.cloneNode(true);
    card.querySelector('[data-cc-coupon]').dataset.offerHandle = offer.handle;
    card.querySelector('[data-cc-coupon-name]').textContent = offer.name;
    card.querySelector('[data-cc-coupon-description]').textContent = offer.description;
    card.querySelector('[data-cc-coupon-subtitle]').textContent = this.termText(offer.subtitle);

    const saving = card.querySelector('[data-cc-coupon-saving]');
    saving.hidden = offer.amount <= 0;
    saving.textContent = offer.amount > 0
      ? this.t('saveOnOrder', { amount: this.money(offer.amount) })
      : '';

    const terms = card.querySelector('[data-cc-coupon-terms]');
    const termTemplate = this.querySelector('[data-cc-coupon-term-template]');
    terms.replaceChildren(...offer.terms.map((term) => {
      const row = termTemplate.content.cloneNode(true);
      row.querySelector('[data-cc-coupon-term]').textContent = this.termText(term);
      return row;
    }));

    /*
     * Terms are whatever the merchant wrote on the offer and nothing else, so an offer with
     * none has an empty list. The View more control has to go with it — left in place it
     * would invite a tap that opens nothing, which reads as broken rather than as empty.
     */
    const toggle = card.querySelector('[data-cc-coupon-toggle]');
    if (toggle) toggle.hidden = offer.terms.length === 0;

    this.tagOfferButton(card.querySelector('[data-cc-offer-apply]'), offer, 'applyOffer');
    return card;
  }

  /**
   * Turns a term descriptor into a sentence. The offer layer sends
   * { key, amount?, percent?, count? } with amounts in paise; the wording and the
   * money formatting are decided here and nowhere else.
   *
   * A `{ text }` descriptor is a line the merchant wrote on the offer itself, so it is
   * already a sentence and is printed as given. Checked before `key` on purpose: passing
   * merchant prose to `t()` would look up a translation key that does not exist and put
   * the raw text on screen only by accident.
   */
  termText(term) {
    if (term?.text) return term.text;
    if (!term?.key) return '';
    const { key, ...values } = term;
    const replacements = Object.fromEntries(Object.entries(values).map(([name, value]) => [
      name,
      name === 'amount' ? this.money(value) : value,
    ]));
    return this.t(key, replacements);
  }

  paintSummary({ sellingTotal, mrpTotal, discount, grandTotal }) {
    /*
     * Total MRP is the only figure a gift now appears in: it is counted there at what it
     * normally sells for, as something the shopper is receiving. Every other figure — Discount
     * on MRP, the struck-through total, the savings line, Coupon discount and the grand
     * total — is the paid goods alone.
     *
     * That is a deliberate choice about wording, not an oversight, and it has a consequence:
     * the breakdown no longer adds up on a cart with a gift. Total MRP is higher than the
     * discounts and grand total account for, by exactly the gift's price. Take the gift out of
     * Total MRP as well and the columns reconcile again.
     */
    const giftWorth = this.giftSavings();
    const mrpWithGift = mrpTotal + giftWorth;

    // The paid goods' own MRP saving, with no gift in it. Both the Discount on MRP row and
    // the savings line report this.
    const paidMrpDiscount = Math.max(mrpTotal - sellingTotal, 0);
    const totalSaved = paidMrpDiscount + discount;

    this.setText('[data-cc-row-mrp]', this.money(mrpWithGift));
    this.setText('[data-cc-row-mrp-discount]', this.money(paidMrpDiscount));
    this.setText('[data-cc-row-coupon]', this.money(discount));
    this.setText('[data-cc-row-grand]', this.money(grandTotal));
    this.setText('[data-cc-total]', this.money(grandTotal));

    /*
     * The struck-through total counts the paid goods only — mrpTotal, not mrpWithGift. It sits
     * beside the amount being charged and reads as "this is what these items would have cost",
     * so a gift's price does not belong in it: the shopper never chose to buy that item and
     * was never going to pay for it.
     *
     * The savings line beneath it is gift-free for the same reason, and the two now reconcile
     * exactly: mrpTotal − grandTotal is precisely paidMrpDiscount + discount, so the gap
     * between the struck price and the total is the number the shopper is told they saved.
     *
     * `discounted` is measured on the same gift-free figure that gets shown. Measured on
     * mrpWithGift instead, a cart with no discount but a gift in it would pass the test and
     * then strike through a price identical to the total.
     */
    const discounted = mrpTotal > grandTotal;

    const was = this.querySelector('[data-cc-total-was]');
    if (was) {
      was.hidden = !discounted;
      was.textContent = discounted ? this.money(mrpTotal) : '';
    }

    /*
     * The figures row carries the state as a class so the stylesheet can size the total:
     * once the struck-through MRP appears beside it the two have to share one line, and at
     * its full size the pair wrapped. Set here rather than inferred in CSS from the `<s>`
     * being unhidden, because every other state in this drawer is a class the script sets.
     */
    this.querySelector('[data-cc-figures]')?.classList.toggle('is-discounted', discounted);

    const saved = this.querySelector('[data-cc-total-saved]');
    if (saved) {
      saved.hidden = totalSaved <= 0;
      saved.textContent = totalSaved > 0
        ? this.t('youSaved', { amount: this.money(totalSaved) })
        : '';
    }

    const note = this.querySelector('[data-cc-saved-note]');
    if (note) {
      /*
       * Numerator and denominator have to describe the same goods. totalSaved is the paid
       * items' saving, so the base is their MRP — mrpWithGift would divide a gift-free saving
       * by a total that includes the gift and quietly understate the percentage.
       *
       * A gift can no longer push this over 100% either: it is absent from both sides.
       */
      const percent = mrpTotal > 0 ? Math.round((totalSaved / mrpTotal) * 100) : 0;
      note.hidden = totalSaved <= 0;
      note.textContent = totalSaved > 0
        ? this.t('youSavedPercent', { amount: this.money(totalSaved), percent })
        : '';
    }
  }


  /* ----------------------------------------------------------------- note */

  /*
   * "Add a note". Writes Shopify's own cart.note, so it reaches the order without anything
   * else having to carry it. Whether Shopflo surfaces it during checkout is unconfirmed,
   * but it is on the cart either way and appears on the order in admin.
   */
  /**
   * All three states of the note row come from here, so they cannot disagree:
   *
   *   closed, no note   "Add a note" with a plus
   *   closed, saved     the note itself with a bin
   *   open              "Add a note" greyed back, neither control
   *
   * The saved value is read from the field's data-saved rather than from cart.note, since
   * a save does not re-render the section.
   */
  paintNote() {
    const note = this.querySelector('[data-cc-note]');
    if (!note) return;

    const open = note.classList.contains('is-open');
    const saved = (this.querySelector('[data-cc-note-field]')?.dataset.saved ?? '').trim();

    const label = this.querySelector('[data-cc-note-label]');
    if (label) label.textContent = !open && saved ? saved : this.t('noteTitle');

    const plus = this.querySelector('[data-cc-note-plus]');
    if (plus) plus.hidden = open || Boolean(saved);

    const remove = this.querySelector('[data-cc-note-delete]');
    if (remove) remove.hidden = open || !saved;
  }

  toggleNote(open) {
    const note = this.querySelector('[data-cc-note]');
    const toggle = this.querySelector('[data-cc-note-toggle]');
    const body = this.querySelector('[data-cc-note-body]');
    if (!note || !toggle || !body) return;

    note.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    body.hidden = !open;
    this.paintNote();

    if (open) this.querySelector('[data-cc-note-field]')?.focus();
  }

  /** Cancel puts back the value as last stored, not whatever was typed. */
  cancelNote() {
    const field = this.querySelector('[data-cc-note-field]');
    if (field) field.value = field.dataset.saved ?? '';
    this.toggleNote(false);
  }

  /**
   * Writes cart.note. Saved on its own rather than through postCart: a note changes no
   * price, so a section re-render and an offer recompute would be wasted work — and the
   * skeleton flashing for it would suggest the cart was recalculating when nothing had.
   *
   * Returns whether it stuck, so the caller can decide what to do about a failure.
   */
  async writeNote(value) {
    try {
      const response = await fetch(`${this.routes}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value }),
      });
      if (!response.ok) throw new Error(`cart/update.js responded ${response.status}`);
      return true;
    } catch (error) {
      console.error('[custom-cart] could not save the note:', error);
      return false;
    }
  }

  /** Left open on failure, so what was typed is not lost. */
  async saveNote() {
    const field = this.querySelector('[data-cc-note-field]');
    const save = this.querySelector('[data-cc-note-save]');
    if (!field) return;

    const note = field.value.trim();
    save?.classList.add('is-busy');

    if (await this.writeNote(note)) {
      field.dataset.saved = note;
      this.toggleNote(false);
    }
    save?.classList.remove('is-busy');
  }

  /** The bin: clears the note and puts the row back to "Add a note". */
  async deleteNote() {
    const field = this.querySelector('[data-cc-note-field]');
    const remove = this.querySelector('[data-cc-note-delete]');
    remove?.classList.add('is-busy');

    if (await this.writeNote('')) {
      if (field) {
        field.value = '';
        field.dataset.saved = '';
      }
      this.toggleNote(false);
    }
    remove?.classList.remove('is-busy');
  }

  /* --------------------------------------------------------------- upsell */

  /*
   * "You might also like". The list, and which products are filtered out of it, are
   * decided in snippets/custom-cart-upsell.liquid — this only handles paging and adding.
   *
   * The viewport does not scroll to touch or wheel (overflow-x is hidden), so the arrows
   * are the whole interface, and scrollTo still works on it programmatically.
   */
  paintUpsell() {
    const viewport = this.querySelector('[data-cc-upsell-viewport]');
    const prev = this.querySelector('[data-cc-upsell-page="prev"]');
    const next = this.querySelector('[data-cc-upsell-page="next"]');
    if (!viewport) return;

    // 1px of slack: sub-pixel widths mean scrollLeft rarely lands exactly on the end.
    const atStart = viewport.scrollLeft <= 1;
    const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 1;
    const fits = viewport.scrollWidth <= viewport.clientWidth + 1;

    if (prev) prev.hidden = fits || atStart;
    if (next) next.hidden = fits || atEnd;
  }

  /** Pages by a whole card plus its gap, so a card never ends up half shown. */
  pageUpsell(direction) {
    const viewport = this.querySelector('[data-cc-upsell-viewport]');
    const card = viewport?.querySelector('.custom-cart-upsell__item');
    if (!viewport || !card) return;

    // The cards are separated by their own padding and a rule, not a flex gap, so the
    // step is exactly one card's outer width.
    const step = card.getBoundingClientRect().width * (direction === 'next' ? 1 : -1);

    viewport.scrollBy({ left: step, behavior: 'smooth' });
    // scrollBy is async; the arrows are re-evaluated by the scroll listener.
  }

  bindUpsell() {
    const viewport = this.querySelector('[data-cc-upsell-viewport]');
    if (!viewport || viewport.dataset.bound === 'true') return;
    viewport.dataset.bound = 'true';
    viewport.addEventListener('scroll', this.handleUpsellScroll, { passive: true });
  }

  /**
   * Adds the first available variant, through the same pipeline as every other change.
   *
   * Goes through add.js rather than update.js because the merchant may choose to keep
   * suggesting products that are already in the cart — and update.js would then set that
   * line's quantity to one rather than adding to it.
   */
  addUpsell(button) {
    const variantId = Number(button?.dataset.variantId);
    if (!variantId) return;

    this.upsellAdded.delete(variantId);
    this.upsellAdded.add(variantId);

    /*
     * Reported before the cart request, like the swap events and for the same reason: postCart()
     * swallows its own failures, so awaiting it would prove nothing about the outcome and would
     * risk losing the event if the shopper navigated mid-request. Read as an intent event.
     *
     * Figures come off the button rather than the cart, so what is reported is exactly what the
     * shopper was shown. mutateAdd() adds one, hence the fixed quantity.
     */
    const track = button.closest('[data-cc-upsell-track]');
    const price = CustomCartDrawer.majorUnits(button.dataset.upsellPrice);

    CustomCartDrawer.pushUpsellEvent({
      // `added` mirrors the swap payload's key, so one GTM variable set can serve both.
      added: {
        product_id: button.dataset.upsellProduct || null,
        product_name: button.dataset.upsellTitle || null,
        variant_id: String(variantId),
        variant_title: button.dataset.upsellVariantTitle || null,
        price,
        quantity: 1,
      },
      value: price,
      source: track?.dataset.ccUpsellSource || null,
      placement: track?.dataset.ccUpsellPlacement || null,
    });

    button.classList.add('is-busy');
    return this.mutateAdd(variantId).then(() => {
      this.showToast(this.t('upsellAdded'));
    });
  }

  /**
   * Orders the track the same way the snippet does — products already in the cart at the
   * end — and then refines it: among those, the ones added from this list go last, most
   * recent of all.
   *
   * The snippet has already put owned products last by the time this runs, so this only has
   * to settle the order between them. It moves a card solely when the snippet marked it
   * `data-cc-upsell-in-cart`, which keeps the two in agreement: a product added from here and
   * later removed from the cart loses the marker and returns to its place in the list, rather
   * than being pinned to the end for the rest of the session.
   */
  reorderUpsell() {
    if (!this.upsellAdded.size) return;

    const track = this.querySelector('[data-cc-upsell-track]');
    if (!track) return;

    for (const variantId of this.upsellAdded) {
      const card = track.querySelector(
        `[data-cc-upsell-item][data-cc-upsell-in-cart][data-variant-id="${variantId}"]`,
      );
      // Iterated in insertion order, so the most recently added finishes last of all.
      if (card) track.append(card);
    }
  }

  showToast(message) {
    const toast = this.querySelector('[data-cc-toast]');
    if (!toast || !message) return;

    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.toastHideTimer);

    this.setText('[data-cc-toast-text]', message);

    toast.classList.remove('is-visible');
    this.flushStyles(toast);
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));

    this.toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      // Hidden only once it has faded, so it cannot be read or focused on the way out.
      this.toastHideTimer = window.setTimeout(() => {
        toast.hidden = true;
      }, this.cssDuration('--cc-toast-fade', 240));
    }, this.cssDuration('--cc-toast-timeout', 3000));
  }

  /* ---------------------------------------------------------------- swap */

  /*
   * Swap for better value. The merchant maps a product to a better-value alternative
   * through the `custom.cart_swap_propduct` metafield; the offer is rendered per line by
   * snippets/custom-cart-swap.liquid.
   *
   * The record of what was swapped lives in the SAME sessionStorage entry that
   * Alpine.store('xCartSwap') uses for the cart page and the old drawer. Sharing the key
   * rather than keeping a private copy is what lets a swap made here still offer its undo
   * over there. Ephemeral on purpose: a new tab starts with no swaps to undo.
   */
  static SWAP_STORAGE_KEY = 'eurus_cart_swap';

  /*
   * Swap analytics — GTM in, CustomerLabs out.
   *
   * The theme only ever pushes to window.dataLayer. The GTM container turns each push into a
   * CustomerLabs `_cl.trackClick()` call, so retargeting or renaming an event downstream is a
   * container change, never a theme deploy.
   *
   * These names are deliberately NOT the names CustomerLabs receives: a GTM custom-event
   * trigger whose name matches the event its own tag sends back fires itself forever.
   *
   * Pushed BEFORE the cart request, not after: postCart() swallows its failures, so awaiting
   * it would prove nothing about the outcome and would only risk losing the event if the
   * shopper navigated mid-request.
   *
   * Setup, payload and the container config: docs/gtm-customerlabs-cart-swap.md
   */
  static SWAP_EVENTS = { apply: 'cart_swap_apply', undo: 'cart_swap_undo' };

  /*
   * "You might also like" add. Same contract as the swap events: pushed here, renamed and
   * forwarded by the GTM container, never sent to CustomerLabs from the theme.
   *
   * The payload carries `source` (static or dynamic) and `placement` (cart or empty_cart) from
   * the track, so the two suggestion sources can be compared. Placement matters because the
   * empty cart is always static — without it the static numbers would be inflated by empty-cart
   * adds and the comparison would be meaningless.
   */
  static UPSELL_EVENT = 'cart_upsell_add';

  /*
   * Minor units (paise) as Shopify states them -> major units, which is what CL reports on.
   * Distinct from the instance money(), which formats a display string, not a number.
   */
  static majorUnits(minorUnits) {
    return Math.round(Number(minorUnits) || 0) / 100;
  }

  /**
   * The only place the theme touches the data layer. Every cart event goes through here so the
   * envelope — event name, currency — is stated once.
   */
  static pushDataLayer(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        currency: window.Shopify?.currency?.active || 'INR',
        ...payload,
      });
    } catch (error) {
      // Reporting an action must never be able to stop one.
      console.warn(`[custom-cart] ${eventName} not sent:`, error);
    }
  }

  static pushSwapEvent(eventName, swapData) {
    CustomCartDrawer.pushDataLayer(eventName, { swap_data: swapData });
  }

  static pushUpsellEvent(upsellData) {
    CustomCartDrawer.pushDataLayer(CustomCartDrawer.UPSELL_EVENT, { upsell_data: upsellData });
  }

  /** swapped-in variant id -> { from: original variant id, qty }. */
  static readSwaps() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(CustomCartDrawer.SWAP_STORAGE_KEY) ?? '{}');
      return stored && typeof stored === 'object' ? stored : {};
    } catch {
      return {};
    }
  }

  /**
   * Written through Alpine's store when it is on the page, so its in-memory copy cannot
   * drift from storage. Falls back to writing the entry directly — the drawer must keep
   * working if the theme's bundle has not loaded, or ever stops shipping that store.
   */
  static writeSwaps(map, mutateStore) {
    const store = window.Alpine?.store?.('xCartSwap');
    if (store && typeof mutateStore === 'function') {
      mutateStore(store);
      return;
    }
    try {
      window.sessionStorage.setItem(CustomCartDrawer.SWAP_STORAGE_KEY, JSON.stringify(map));
    } catch (error) {
      console.error('[custom-cart] could not record the swap:', error);
    }
  }

  /**
   * Shows the undo control on any line the shopper swapped into, and decides whether that
   * line may also offer a further swap. Runs after every render, since which line qualifies is
   * browser state the server cannot know.
   *
   * Swaps chain. If A nominates B and B nominates C, then a line swapped into B shows Undo AND
   * the offer to go on to C — the shopper can walk the ladder, and Undo walks back down it one
   * rung at a time, because each swap leaves its own record.
   *
   * What is refused is going backwards by swapping. Any variant the shopper has already swapped
   * away from is off the table as a destination: if B nominates A, or C nominates A, taking it
   * would put the shopper back where they were with no record of it, and repeatedly so. Undo is
   * the way back, and it is the only way back.
   */
  /**
   * Everything one line was swapped from, walking its own records back to the start.
   *
   * A→B→C gives C the ancestors {B, A}. Only these are off-limits as swap targets for that
   * line — offering one would walk its chain backwards, which is Undo's job.
   *
   * Scoped to the line on purpose. A variant swapped away from on ONE line says nothing about
   * another: swap A→B, then add C whose metafield offers A, and C→A is a perfectly good swap
   * the shopper has never made.
   *
   * The seen-check ends the walk on a malformed record that points in a circle.
   */
  static swapAncestors(swaps, variantId) {
    const ancestors = new Set();
    let current = String(variantId);

    while (swaps[current]) {
      const from = String(swaps[current].from ?? '');
      if (!from || ancestors.has(from)) break;
      ancestors.add(from);
      current = from;
    }

    return ancestors;
  }

  /**
   * The paid cart line holding a variant, or null.
   *
   * Paid only: a free-gift row carries no `data-cc-line`, so a gift of the same variant can
   * never be picked up here and quietly have its quantity rewritten.
   */
  paidLineFor(variantId) {
    const wanted = Number(variantId);
    return [...this.querySelectorAll('[data-cc-line]')]
      .find((line) => Number(line.dataset.variantId) === wanted) ?? null;
  }

  /** What a rendered line's stepper currently reads. */
  static lineQuantity(line) {
    return Number(line?.querySelector('[data-cc-qty-value]')?.textContent) || 0;
  }

  /**
   * The variants a swap target actually puts in the cart.
   *
   * A combo product is sold as one line but delivered as its components, so swapping into one
   * has to add those instead. combo-products.js owns that map and rewrites /cart/add requests
   * with it — but a swap posts to /cart/update.js, which it never sees, so the expansion is
   * done here from the same source.
   *
   * Anything that is not a combo, or a page where the feature is switched off, comes back as
   * the target itself.
   */
  static swapTargets(variantId) {
    const components = window.ComboProducts?.componentsFor?.(variantId);
    return components?.length ? components : [variantId];
  }

  /**
   * The `updates` body for moving `quantity` off one line and onto one or more variants.
   *
   * cart/update.js sets ABSOLUTE quantities, so a bare `{ [variantId]: quantity }` overwrites
   * whatever the cart already held. Swapping C into A while the cart already has one A would
   * set A to 1 rather than 2 — the swapped unit silently vanishing.
   *
   * Where a destination already has a line, that line's own key is updated to its current
   * quantity plus the moved one. A variant id is used only to create a line that does not
   * exist yet, which also keeps the update off any gift line sharing the variant.
   */
  swapUpdates({ fromLine, toVariants, quantity, leaveOnFromLine = 0 }) {
    const fromKey = fromLine.dataset.lineKey;
    const fromVariant = Number(fromLine.dataset.variantId);
    const updates = { [fromKey]: leaveOnFromLine };

    for (const target of toVariants) {
      const variantId = Number(target);
      if (!variantId) continue;

      // A target that is the line being swapped out stays on that line. Opening a second line
      // for the same variant would be rejected as a duplicate anyway.
      if (variantId === fromVariant) {
        updates[fromKey] += quantity;
        continue;
      }

      const destination = this.paidLineFor(variantId);
      if (destination && destination !== fromLine) {
        updates[destination.dataset.lineKey] =
          CustomCartDrawer.lineQuantity(destination) + quantity;
      } else {
        // `|| 0` so a component listed twice in one combo adds up rather than overwrites.
        updates[variantId] = (updates[variantId] || 0) + quantity;
      }
    }

    return updates;
  }

  paintSwaps() {
    const swaps = CustomCartDrawer.readSwaps();

    for (const line of this.querySelectorAll('[data-cc-line]')) {
      const swapped = Object.prototype.hasOwnProperty.call(swaps, String(line.dataset.variantId));

      const undo = line.querySelector('[data-cc-swap-undo-wrap]');
      if (undo) undo.hidden = !swapped;

      /*
       * Judged on where the offer would take THIS line, against THIS line's own history. That
       * lets a chain continue, refuses to walk it backwards, and leaves every other line alone.
       *
       * The button and the preview are hidden, not their container: the undo control now sits
       * inside it, beside the button, and hiding the container would take the undo with it —
       * exactly when the shopper needs it, on a line whose onward swap is refused.
       */
      const offer = line.querySelector('[data-cc-swap]');
      if (offer) {
        const ancestors = CustomCartDrawer.swapAncestors(swaps, line.dataset.variantId);
        const blocked = ancestors.has(String(offer.dataset.swapTo));
        offer.querySelector('[data-cc-swap-apply]')?.toggleAttribute('hidden', blocked);
        offer.querySelector('[data-cc-swap-preview]')?.toggleAttribute('hidden', blocked);
      }
    }
  }

  /**
   * Remove the current line and add the mapped one at the same quantity, in a single
   * request. Going through postCart means the offer engine recomputes and the cart
   * repaints exactly as it does for any other change — the discounts, the freebie
   * threshold and the totals all follow the swap without anything extra.
   */
  async applySwap(offer) {
    const line = offer?.closest('[data-cc-line]');
    if (!line) return;

    const to = Number(offer.dataset.swapTo);
    const from = Number(offer.dataset.swapFrom);
    const quantity = Number(offer.dataset.swapQuantity) || 1;
    if (!to || !from) return;

    /*
     * What the line being removed IS. Kept in the swap record so an undo can still name it
     * after its cart line is gone — see undoSwap. Prices are the variant's own, never the
     * offer price, so the same product reports the same figure in both directions.
     */
    const removed = {
      product_id: offer.dataset.swapFromProduct || null,
      product_name: offer.dataset.swapFromTitle || null,
      variant_id: String(from),
      variant_title: offer.dataset.swapFromVariantTitle || null,
      price: CustomCartDrawer.majorUnits(offer.dataset.swapFromPrice),
    };

    const added = {
      product_id: offer.dataset.swapToProduct || null,
      product_name: offer.dataset.swapToTitle || null,
      variant_id: String(to),
      variant_title: offer.dataset.swapToVariantTitle || null,
      price: CustomCartDrawer.majorUnits(offer.dataset.swapToPrice),
    };

    CustomCartDrawer.pushSwapEvent(CustomCartDrawer.SWAP_EVENTS.apply, {
      // `removed` is what leaves the cart, `added` what takes its place.
      removed: { ...removed, quantity },
      added: {
        ...added,
        quantity,
        // What the swap card promised, when a cart offer brings the price below the
        // variant's own. Reported beside `price`, never in place of it.
        offer_price: CustomCartDrawer.majorUnits(offer.dataset.swapToOfferPrice),
      },
      // The cart's net change at variant prices, so a swap can be read as a value event.
      value: CustomCartDrawer.majorUnits(
        (Number(offer.dataset.swapToPrice) - Number(offer.dataset.swapFromPrice)) * quantity,
      ),
    });

    line.classList.add('is-leaving');

    /*
     * What the cart will actually hold. For a combo that is its components, not the combo, so
     * the record has to be keyed to a line that will exist — nothing carries the combo's own
     * variant id afterwards, and Undo is found by matching a line's variant against these keys.
     *
     * The first component carries it, and `combo` lists the rest so an undo can take them all
     * back together. One key, so only one Undo appears rather than one per component.
     */
    const targets = CustomCartDrawer.swapTargets(to);
    const record = { from: String(from), qty: quantity, meta: removed };
    if (targets.length > 1) record.combo = targets.map(String);

    CustomCartDrawer.writeSwaps(
      { ...CustomCartDrawer.readSwaps(), [targets[0]]: record },
      (store) => store.record(targets[0], from, quantity, removed),
    );

    await this.mutateUpdates(this.swapUpdates({
      fromLine: line,
      toVariants: targets,
      quantity,
    }));
  }

  /** Put back what was swapped away, at the quantity it was swapped at. */
  async undoSwap(button) {
    const line = button?.closest('[data-cc-line]');
    if (!line) return;

    const variantId = String(line.dataset.variantId);
    const entry = CustomCartDrawer.readSwaps()[variantId];
    if (!entry) return;

    const undoWrap = button.closest('[data-cc-swap-undo-wrap]');
    const quantity = Number(entry.qty) || 1;
    /*
     * Exact mirror of the apply payload: `removed` is the swapped-in line going away,
     * `added` the original coming back. The original's details are read from the swap
     * record, written by applySwap — its cart line no longer exists to read them from.
     *
     * Only a swap made on the cart page (assets/theme.js), or one recorded before this
     * instrumentation shipped, has no meta; those still report a variant id alone.
     */
    const restored = entry.meta || {};
    CustomCartDrawer.pushSwapEvent(CustomCartDrawer.SWAP_EVENTS.undo, {
      removed: {
        product_id: undoWrap?.dataset.undoProduct || null,
        product_name: undoWrap?.dataset.undoTitle || null,
        variant_id: variantId,
        variant_title: undoWrap?.dataset.undoVariantTitle || null,
        price: CustomCartDrawer.majorUnits(undoWrap?.dataset.undoPrice),
        quantity,
      },
      added: {
        product_id: restored.product_id ?? null,
        product_name: restored.product_name ?? null,
        variant_id: String(entry.from),
        variant_title: restored.variant_title ?? null,
        price: restored.price ?? null,
        quantity,
      },
      // Undoing gives back what the swap took, so the value moves the other way.
      value: restored.price == null
        ? null
        : Math.round((restored.price - CustomCartDrawer.majorUnits(undoWrap?.dataset.undoPrice)) * quantity * 100) / 100,
    });

    line.classList.add('is-leaving');
    const remaining = { ...CustomCartDrawer.readSwaps() };
    delete remaining[variantId];
    CustomCartDrawer.writeSwaps(remaining, (store) => store.forget(variantId));

    /*
     * Only this swap's units leave the line. Zeroing it would take any of the same product the
     * shopper already had — swap C onto an A they were already buying and the line holds two,
     * one of which was never part of the swap. Undo must give back one, not empty the line.
     */
    const updates = this.swapUpdates({
      fromLine: line,
      toVariants: [Number(entry.from)],
      quantity,
      leaveOnFromLine: Math.max(CustomCartDrawer.lineQuantity(line) - quantity, 0),
    });

    /*
     * A swap into a combo put several lines in the cart, and undoing it has to take back every
     * one of them — not just the line the button sits on. The others give up the same quantity,
     * and only that quantity, so anything the shopper was already buying stays.
     */
    for (const componentId of entry.combo ?? []) {
      if (Number(componentId) === Number(line.dataset.variantId)) continue;

      const componentLine = this.paidLineFor(componentId);
      if (!componentLine) continue;

      updates[componentLine.dataset.lineKey] =
        Math.max(CustomCartDrawer.lineQuantity(componentLine) - quantity, 0);
    }

    await this.mutateUpdates(updates);
  }

  /* ------------------------------------------------------------- free gift */

  /**
   * What a gift is worth: its compare-at price when there is one, otherwise what it
   * normally sells for. The single definition — the struck-through price on the gift
   * line, the one in the picker and the savings total all read from here, so they
   * cannot drift apart.
   */
  static giftWorth(gift) {
    return Math.max(gift.compareAt ?? 0, gift.price ?? 0);
  }

  /**
   * The total worth of the gifts the shopper is getting, or 0.
   *
   * Both kinds count, because a gift reaches the shopper one of two ways. A gift they picked
   * is a real cart line, which Liquid has already totalled into data-gift-total. A gift the
   * merchant picked is never in the cart — Shopflo adds it at checkout — so its worth is read
   * off the offer.
   */
  /**
   * Whether this offer's gift belongs in the Shopify cart.
   *
   * Only a gift the SHOPPER picks ever did — their choice cannot reach checkout any other
   * way — and GIFT_AS_CART_LINE can switch even that off. Everything else about the gift
   * flow reads this rather than `shopperChosen`, so there is one answer, not several.
   */
  writesGiftLine(offer) {
    return Boolean(GIFT_AS_CART_LINE && offer?.shopperChosen);
  }

  /**
   * Whether there is actually a decision for the shopper to make.
   *
   * One gift option is not a choice. Asking them to select the only thing on offer and then
   * press Confirm is two taps for no decision, so it is taken on their behalf instead — no
   * prompt, no picker, the gift simply appears.
   *
   * Independent of how the offer is applied: whether the shopper typed a code or it switched on
   * by itself, a single option is still not a choice.
   */
  giftNeedsChoosing(offer) {
    return Boolean(offer?.shopperChosen && (offer.gifts?.length ?? 0) > 1);
  }

  /** The gifts to draw for an offer whose gift is not a cart line, as [{variantId, quantity}]. */
  giftDisplayItems(offer) {
    if (!this.giftNeedsChoosing(offer)) return this.autoGiftItems(offer);
    return this.chosenGifts().map(({ gift, quantity }) => ({ variantId: gift.variantId, quantity }));
  }

  giftSavings() {
    const inCart = Number(this.dataset.giftTotal) || 0;

    /*
     * A merchant-picked gift is worked out from the offer, not from the rows on screen.
     * applyOfferResult paints the summary before it calls syncGift, and a refresh has just
     * wiped the panel, so on the first paint those rows do not exist yet — reading them would
     * leave the gift out of the totals until something else triggered a repaint.
     */
    const offer = window.CartOffers?.eligibleGiftOffer();
    if (!offer || this.writesGiftLine(offer)) return inCart;

    return inCart + this.giftDisplayItems(offer).reduce((sum, { variantId, quantity }) => {
      const gift = offer.gifts?.find((candidate) => candidate.variantId === variantId);
      return sum + (gift ? gift.price * quantity : 0);
    }, 0);
  }

  /**
   * The gifts the shopper holds right now, as [{ gift, quantity }].
   *
   * One place decides this, because the cart line, the savings figure and the picker's
   * opening state all have to agree. A stored choice only counts while it belongs to the
   * offer that is actually live, and a variant that has since left the offer is dropped.
   */
  chosenGifts() {
    const offer = window.CartOffers?.eligibleGiftOffer();
    if (!offer) return [];

    /*
     * Two possible sources, one answer. When the gift is a cart line the cart is the truth, so
     * it is read back off the rendered rows. When it is not, the choice has nowhere else to
     * live and is kept in localStorage.
     */
    const held = this.writesGiftLine(offer)
      ? [...this.querySelectorAll('[data-cc-gift-line]')]
        .filter((line) => line.dataset.giftOffer === offer.handle)
        .map((line) => ({
          variantId: Number(line.dataset.variantId),
          quantity: Number(line.dataset.giftQuantity) || 0,
        }))
      : CustomCartDrawer.readGiftChoice(offer.handle);

    // A variant that has since left the offer — sold out, or the merchant removed it — is
    // dropped rather than shown as something the shopper is getting.
    return held
      .map(({ variantId, quantity }) => ({
        gift: offer.gifts.find((candidate) => candidate.variantId === variantId),
        quantity,
      }))
      .filter((entry) => entry.gift && entry.quantity > 0);
  }

  /**
   * The shopper's gift choice, when it is not a cart line.
   *
   * Stored against the offer handle, so a choice made for one offer is never shown as a choice
   * for another. localStorage rather than sessionStorage: a gift they picked is theirs to keep
   * across visits, the same as an entered coupon code.
   */
  static readGiftChoice(handle) {
    try {
      const stored = JSON.parse(window.localStorage.getItem(GIFT_CHOICE_KEY) ?? 'null');
      if (!stored || stored.handle !== handle || !Array.isArray(stored.items)) return [];
      return stored.items;
    } catch {
      return [];
    }
  }

  static writeGiftChoice(handle, items) {
    try {
      window.localStorage.setItem(GIFT_CHOICE_KEY, JSON.stringify({ handle, items }));
    } catch (error) {
      console.error('[custom-cart] could not save the gift choice:', error);
    }
  }

  async writeGiftLines(handle, items) {
    if (this.giftWriteInFlight) return;
    this.giftWriteInFlight = true;
    this.setBusy(true);
    try {
      const sections = this.themeSections();
      const sectionIds = sections.map((section) => section.id);
      const withSections = (body) => ({
        ...body, sections: sectionIds, sections_url: window.location.pathname,
      });

      const current = await this.cartRequest('cart.js');
      const stale = (current.items ?? []).filter((item) => item.properties?._gift);
      let last = null;

      if (stale.length) {
        last = await this.cartRequest('cart/update.js', withSections({
          updates: Object.fromEntries(stale.map((line) => [line.key, 0])),
        }));
      }

      if (items.length && handle) {
        last = await this.cartRequest('cart/add.js', withSections({
          items: items.map(({ variantId, quantity }) => ({
            id: variantId,
            quantity,
            properties: { _gift: handle },
          })),
        }));
      }

      if (last) this.applyThemeSections(sections, last.sections);

      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      await this.refresh();
      this.syncThemeCount();
    } catch (error) {
      console.error('[custom-cart] could not update the gift lines:', error);
      await this.refresh();
    } finally {
      this.giftWriteInFlight = false;
      this.setBusy(false);
    }
  }

  async cartRequest(route, body) {
    const response = await fetch(this.cartUrl(route), {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error(`${route} responded ${response.status}`);
    return response.json();
  }

  syncGift() {
    const offer = window.CartOffers?.eligibleGiftOffer() ?? null;
    this.activeGiftOffer = offer;

    if (!offer) {
      if (this.querySelector('[data-cc-gift-line]')) this.writeGiftLines(null, []);
      this.clearGiftDisplay();
      this.giftPromptedFor = null;
      return;
    }

    if (!this.writesGiftLine(offer)) {
      /*
       * Display only — deliberately NOT written to the cart. Either the merchant picks the
       * gift, in which case Shopflo's own offer adds the product during checkout, or
       * GIFT_AS_CART_LINE is off.
       *
       * Any gift line already in the cart is removed either way. Left there it would arrive at
       * checkout as an ordinary paid product, and the shopper would be charged for something
       * this drawer showed as free.
       */
      if (this.querySelector('[data-cc-gift-line]')) this.writeGiftLines(null, []);

      const wanted = this.giftDisplayItems(offer);
      this.renderGiftDisplay(offer, wanted);

      if (this.giftNeedsChoosing(offer)) {
        /*
         * The shopper still chooses — only the cart write is suppressed — so the prompt is
         * still owed until they have. Same once-per-offer rule as the cart-line path.
         */
        if (!wanted.length && this.giftPromptedFor !== offer.handle && this.classList.contains('is-open')) {
          this.giftPromptedFor = offer.handle;
          this.giftPromptDue = true;
        }
        return;
      }

      if (!wanted.length && this.giftConfigWarnedFor !== offer.handle) {
        // Nothing to give: the offer's products are unavailable or it lists none. Said once
        // per offer, since syncGift runs on every repaint.
        this.giftConfigWarnedFor = offer.handle;
        console.error(`[custom-cart] "${offer.handle}" picks the gift itself but has no available gift to give`);
      }

      this.giftPromptedFor = null;
      return;
    }

    // A gift the shopper picks is a real cart line, so no display rows belong on screen.
    this.clearGiftDisplay();

    const chosen = this.chosenGifts();

    const held = chosen.reduce((sum, entry) => sum + entry.quantity, 0);
    if (held > this.giftLimit()) {
      this.writeGiftLines(offer.handle, this.trimToGiftLimit(chosen));
      return;
    }

    /*
     * Only one thing on offer, so it is written straight to the cart instead of prompted for.
     *
     * Converges without a comparison: the write refreshes, syncGift runs again, and by then
     * `chosen` holds that gift, so the condition is false and nothing writes a second time.
     */
    if (!this.giftNeedsChoosing(offer)) {
      if (!chosen.length) {
        const only = this.autoGiftItems(offer);
        if (only.length) this.writeGiftLines(offer.handle, only);
      }
      return;
    }

    /*
     * Asked once per session per offer. Skipping is remembered, and the shopper can come
     * back through the gift line's edit button or the coupon drawer.
     *
     * Only flagged here, not opened. Gift state has to settle before anything is
     * announced — otherwise a gift that just lapsed gets celebrated — but the prompt
     * should still come second, after any celebration. applyOfferResult opens it once
     * both have had their turn.
     */
    if (!chosen.length && this.giftPromptedFor !== offer.handle && this.classList.contains('is-open')) {
      this.giftPromptedFor = offer.handle;
      this.giftPromptDue = true;
    }
  }

  /**
   * The "you have unlocked a freebie" step, which is the only way into the picker.
   *
   * No fallback to opening the picker directly: that was a second route to the same
   * modal and could put it on screen alongside this one. The prompt is unconditional in
   * the template, so if it is missing the template is wrong and should say so.
   */
  promptForGift() {
    const sheet = this.querySelector('[data-cc-sheet="gift-unlock"]');
    if (!sheet) {
      console.error('[custom-cart] the gift prompt sheet is missing from the section');
      return;
    }
    this.openSheet(sheet);
  }

  /**
   * Select items — hand over from the prompt to the picker once it has slid away.
   *
   * The picker jumps whatever is waiting: the shopper asked for it just now, so it
   * should not appear behind a celebration that happened to fall due first. Anything
   * queued is put back and takes its turn after the picker closes.
   */
  async startGiftSelection() {
    const waiting = this.sheetQueue.splice(0);
    await this.closeSheet();
    this.sheetQueue.push(...waiting);
    this.openGiftSheet();
  }

  openGiftSheet() {
    const offer = this.activeGiftOffer;
    // Nothing to choose between — the merchant picked it, or there is only one option.
    if (offer && !this.giftNeedsChoosing(offer)) return;

    const sheet = this.querySelector('[data-cc-sheet="gifts"]');
    const list = this.querySelector('[data-cc-gift-list]');
    const template = this.querySelector('[data-cc-gift-option-template]');
    if (!offer || !sheet || !list || !template) return;

    // The draft is variantId -> quantity. A Map keeps the order the shopper picked in.
    this.giftDraft = new Map(this.chosenGifts().map(({ gift, quantity }) => [gift.variantId, quantity]));

    const options = offer.gifts.map((gift) => {
      const option = template.content.cloneNode(true);
      const root = option.querySelector('[data-cc-gift-option]');
      root.dataset.variantId = String(gift.variantId);

      const image = option.querySelector('img');
      if (image) {
        image.src = gift.image;
        image.alt = gift.title;
      }
      const title = [gift.title, gift.variantTitle].filter(Boolean).join(' — ');
      option.querySelector('[data-cc-gift-title]').textContent = title;
      option.querySelector('[data-cc-gift-was]').textContent =
        this.money(CustomCartDrawer.giftWorth(gift));

      // Named labels, so a screen reader hears which gift each control belongs to.
      option.querySelector('[data-cc-gift-add]')
        ?.setAttribute('aria-label', this.t('add_gift', { title }));
      option.querySelector('[data-cc-gift-step="up"]')
        ?.setAttribute('aria-label', this.t('increase_gift', { title }));
      option.querySelector('[data-cc-gift-step="down"]')
        ?.setAttribute('aria-label', this.t('decrease_gift', { title }));
      return option;
    });

    list.replaceChildren(...options);
    this.paintGiftDraft();
    this.openSheet(sheet);
  }

  trimToGiftLimit(chosen) {
    let left = this.giftLimit();
    const kept = [];
    for (const { gift, quantity } of chosen) {
      if (left <= 0) break;
      const take = Math.min(quantity, left);
      kept.push({ variantId: gift.variantId, quantity: take });
      left -= take;
    }
    return kept;
  }

  /**
   * Draws the gifts a merchant-picked offer gives, as rows that look like cart lines but are
   * not in the cart. Shopflo adds the real product at checkout; these only tell the shopper
   * what is coming.
   *
   * Rebuilt from scratch on each repaint rather than diffed. There is no cart write behind it,
   * so redrawing costs one clone per gift and cannot drift out of step with the offer.
   *
   * Appended after the paid lines, since a gift is not part of what was chosen.
   */
  renderGiftDisplay(offer, items) {
    this.clearGiftDisplay();

    const list = this.querySelector('[data-cc-lines]');
    const template = this.querySelector('[data-cc-gift-display-template]');
    if (!list || !template || !items.length) return;

    for (const { variantId, quantity } of items) {
      const gift = offer.gifts?.find((candidate) => candidate.variantId === variantId);
      if (!gift) continue;

      const row = template.content.cloneNode(true);
      const line = row.querySelector('[data-cc-gift-display]');

      line.dataset.giftOffer = offer.handle;
      line.dataset.variantId = String(variantId);
      line.dataset.giftQuantity = String(quantity);

      const image = row.querySelector('[data-cc-gift-display-image]');
      if (image && gift.image) image.src = gift.image;
      else image?.remove();

      this.setNodeText(row, '[data-cc-gift-display-title]', gift.title);
      this.setNodeText(row, '[data-cc-gift-display-qty]', String(quantity));

      const variant = row.querySelector('[data-cc-gift-display-variant]');
      if (variant && gift.variantTitle) {
        variant.hidden = false;
        variant.textContent = gift.variantTitle;
      }

      // Only a gift the shopper picked can be changed.
      const edit = row.querySelector('[data-cc-gift-display-edit]');
      if (edit) edit.hidden = !this.giftNeedsChoosing(offer);

      list.append(row);
    }
  }

  clearGiftDisplay() {
    for (const row of this.querySelectorAll('[data-cc-gift-display]')) row.remove();
  }

  /** Sets text inside a cloned fragment, which setText cannot reach — it searches from this. */
  setNodeText(root, selector, text) {
    const node = root.querySelector(selector);
    if (node) node.textContent = text;
  }

  autoGiftItems(offer) {
    const gifts = offer.gifts ?? [];
    if (!gifts.length) return [];

    const counts = new Map();
    const limit = Math.max(offer.rewardQuantity ?? 1, 1);
    for (let taken = 0; taken < limit; taken += 1) {
      const { variantId } = gifts[taken % gifts.length];
      counts.set(variantId, (counts.get(variantId) ?? 0) + 1);
    }
    return [...counts].map(([variantId, quantity]) => ({ variantId, quantity }));
  }

  /** Whether the cart already holds exactly these gift lines, and nothing else. */
  /** How many gifts this offer allows in total, across all of them. */
  giftLimit() {
    return this.activeGiftOffer?.rewardQuantity ?? 1;
  }

  giftTaken() {
    return [...this.giftDraft.values()].reduce((sum, quantity) => sum + quantity, 0);
  }

  /**
   * Moves one gift's quantity by `delta`, within the offer's allowance.
   *
   * The allowance is a hard ceiling at every size, one included. Once it is used up the
   * other gifts are refused and shown as unavailable, so a shopper who wants a different
   * one has to step the chosen gift down first.
   *
   * This used to swap on tap when the allowance was one, treating it as a radio group. The
   * ceiling now behaves the same way whatever its size, so what "full" means never depends
   * on how many gifts the offer happens to allow.
   */
  changeGiftDraft(option, delta) {
    if (!option) return;

    const variantId = Number(option.dataset.variantId);
    const current = this.giftDraft.get(variantId) ?? 0;
    const next = Math.max(current + delta, 0);

    if (delta > 0 && this.giftTaken() - current + next > this.giftLimit()) return;

    if (next === 0) this.giftDraft.delete(variantId);
    else this.giftDraft.set(variantId, next);

    this.paintGiftDraft();
  }

  paintGiftDraft() {
    const limit = this.giftLimit();
    const taken = this.giftTaken();
    const full = taken >= limit;

    for (const option of this.querySelectorAll('[data-cc-gift-option]')) {
      const quantity = this.giftDraft.get(Number(option.dataset.variantId)) ?? 0;
      const selected = quantity > 0;

      option.classList.toggle('is-selected', selected);
      // Every unchosen gift goes unavailable once the allowance is used up, at any size.
      option.classList.toggle('is-blocked', full && !selected);

      const add = option.querySelector('[data-cc-gift-add]');
      if (add) {
        add.hidden = selected;
        add.disabled = full && !selected;
      }

      const stepper = option.querySelector('[data-cc-gift-stepper]');
      if (stepper) stepper.hidden = !selected;

      const value = option.querySelector('[data-cc-gift-qty]');
      if (value) value.textContent = String(quantity || 1);

      const up = option.querySelector('[data-cc-gift-step="up"]');
      if (up) up.disabled = full;
    }

    this.setText('[data-cc-gift-counter]', this.t('giftCounter', {
      selected: taken,
      total: limit,
    }));
  }

  confirmGiftSelection() {
    const offer = this.activeGiftOffer;
    // The picker cannot open for a merchant-picked offer, so this should be unreachable — but
    // acting on a confirmation for one would either write a line that gets charged for or store
    // a choice nobody made, so it is guarded rather than assumed.
    if (!offer || !offer.shopperChosen) return;

    const items = [...this.giftDraft].map(([variantId, quantity]) => ({ variantId, quantity }));
    this.closeSheet();

    if (this.writesGiftLine(offer)) {
      this.writeGiftLines(offer.handle, items);
    } else {
      // No cart line to hold the choice, so it goes to localStorage and the repaint below
      // draws it as display rows.
      CustomCartDrawer.writeGiftChoice(offer.handle, items);
    }

    /*
     * A full repaint rather than just the gift line. Accepting a gift changes the
     * summary — its worth is added to the savings figure — as well as the offer row and
     * the coupon list, and it earns the celebration. Painting the pieces by hand here is
     * what left the savings total stale before.
     *
     * syncGift inside this call draws the gift line, so there is no need to do it above.
     */
    const result = window.CartOffers?.result;
    if (result) this.applyOfferResult(result);
  }

  /* ------------------------------------------------------- celebration */

  /**
   * Fires whenever something new starts working on this cart: an automatic discount, a
   * code the shopper entered, or a free gift they chose. A gift counts even though it
   * discounts nothing — accepting it is the same act from the shopper's side.
   *
   * Compared against the previous set rather than everything seen this session, so an
   * offer that lapses and returns is celebrated again. That matters now the shopper can
   * remove an offer and apply it a second time.
   */
  celebrateNewOffers(result, listing) {
    const applied = new Map(listing.applied.map((offer) => [offer.handle, offer]));
    const fresh = [...applied.keys()].filter((handle) => !this.celebratedHandles.has(handle));
    this.celebratedHandles = new Set(applied.keys());
    CustomCartDrawer.writeCelebrated(this.celebratedHandles);

    // Nothing new, or the drawer is closed — no point celebrating either way. There is
    // no longer a check for the coupon panel: sheets sit outside the cart panel now and
    // paint above it, and skipping the celebration would have dropped it for good,
    // since the handles are already marked as seen above.
    if (!fresh.length || !this.classList.contains('is-open')) return;

    const sheet = this.querySelector('[data-cc-sheet="celebrate"]');
    if (!sheet) return;

    const best = fresh
      .map((handle) => applied.get(handle))
      .reduce((a, b) => (b.amount > a.amount ? b : a));

    const saved = Math.max(this.savedTotal(result), 0);
    this.setText('[data-cc-celebrate-offer]', best.name);
    this.setText('[data-cc-celebrate-amount]', this.money(saved));
    // Reuses the same wording as the summary's own savings line, so the shopper is told the
    // figure the same way in both places.
    this.setText('[data-cc-celebrate-saved]', this.t('youSaved', { amount: this.money(saved) }));

    // A gift on an undiscounted cart saves nothing countable, and "Total savings Rs 0"
    // would read as a fault. Celebrate the offer and leave every figure out — including the
    // rule between them, which has nothing left to separate.
    const nothingSaved = saved <= 0;
    for (const selector of [
      '[data-cc-celebrate-amount]',
      '[data-cc-celebrate-label]',
      '[data-cc-celebrate-saved]',
      '[data-cc-celebrate-divider]',
    ]) {
      const element = this.querySelector(selector);
      if (element) element.hidden = nothingSaved;
    }

    /*
     * The burst is tied to the sheet appearing, not to the decision to celebrate. Fired
     * straight after openSheet it went off even when the sheet was queued behind another
     * one — confetti over the freebie prompt, with nothing to explain it.
     */
    this.openSheet(sheet, {
      autoClose: true,
      onOpen: () => window.CustomCartConfetti?.burst(this),
    });
  }

  /*
   * Which offers have already been celebrated survives a page load, so opening the cart
   * again on a new page does not replay the confetti for a discount the shopper has
   * already been shown. It is deliberately per-session: a new visit is a new occasion.
   *
   * Note this is written on every repaint, and a repaint only happens while the drawer
   * is open. A discount picked up from a product page therefore still counts as new
   * when the shopper opens the cart, which is exactly when it should be announced.
   */
  static CELEBRATED_STORAGE_KEY = 'tss_cart_celebrated';

  static readCelebrated() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(CustomCartDrawer.CELEBRATED_STORAGE_KEY) ?? '[]');
      return new Set(Array.isArray(stored) ? stored : []);
    } catch {
      return new Set();
    }
  }

  static writeCelebrated(handles) {
    try {
      window.sessionStorage.setItem(
        CustomCartDrawer.CELEBRATED_STORAGE_KEY,
        JSON.stringify([...handles]),
      );
    } catch (error) {
      console.error('[custom-cart] could not record the celebrated offers:', error);
    }
  }

  /** Everything off the MRP: the compare-at reduction plus every applied offer. */
  savedTotal(result) {
    const mrpTotal = Number(this.dataset.mrpTotal || 0);
    const sellingTotal = Number(this.dataset.sellingTotal || 0);
    return mrpTotal - (sellingTotal - result.totalDiscount);
  }

  setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  /* --------------------------------------------------------------- coupons */

  /**
   * Applies a typed code. Both coupon inputs use this; each names its own field and
   * error line so the two never write over each other.
   */
  async applyCoupon(
    inputSelector = '[data-cc-coupon-input]',
    errorSelector = '[data-cc-coupon-error]',
  ) {
    const input = this.querySelector(inputSelector);
    const error = this.querySelector(errorSelector);
    const code = input?.value.trim();
    if (!code) return;

    if (error) error.hidden = true;

    const result = await this.whileBusy(() => window.CartOffers?.applyCode(code));
    const matched = result?.appliedOffers.some((offer) => offer.mode === 'code');

    if (!matched) {
      await this.whileBusy(() => window.CartOffers?.removeCode(code));
      if (error) {
        error.hidden = false;
        error.textContent = this.t('couponInvalid');
      }
      return;
    }

    if (input) {
      input.value = '';
      const apply = this.querySelector(input.dataset.ccApplyTarget);
      if (apply) apply.hidden = true;
    }
    await this.syncCouponAttribute();
    await this.closeCoupons();
    this.applyOfferResult(result);
  }

  /**
   * Applies one named offer. Returning to the cart first is deliberate: the shopper
   * sees the price change and the celebration on the screen they came from, which is
   * also what makes the celebration sheet paint above everything.
   *
   * A gift offer has no discount to apply — accepting it means choosing the gift, so
   * the picker opens instead.
   */
  async applyOffer(handle, kind) {
    if (!handle) return;
    await this.closeCoupons();

    /*
     * Accepting a gift offer opens nothing directly. Clearing giftPromptedFor is enough:
     * the recompute below repaints, syncGift sees an eligible offer with nothing chosen,
     * and puts up the unlock prompt — which is the only way into the picker.
     *
     * Opening the picker here as well is what put two sheets on screen at once.
     */
    if (kind === 'gift') this.giftPromptedFor = null;

    await this.whileBusy(async () => {
      await window.CartOffers?.applyOffer(handle);
      await this.syncCouponAttribute();
    });
  }

  /** Removing keeps the shopper where they are, so they can pick something else. */
  async removeOffer(handle) {
    if (!handle) return;
    await this.whileBusy(async () => {
      await window.CartOffers?.removeOffer(handle);
      await this.syncCouponAttribute();
    });
  }

  /**
   * Best effort: hand the entered codes to checkout through a cart attribute. Written
   * from the stored list rather than from whatever was just typed, so removing one of
   * two codes leaves the other one in place. Whether Shopflo reads this attribute is
   * still unconfirmed, so the shopper may need to enter the code again at checkout.
   */
  async syncCouponAttribute() {
    const codes = window.CartOffers?.codes ?? [];
    try {
      await fetch(`${this.routes}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Coupon Code': codes.join(', ') || null } }),
      });
    } catch (error) {
      console.error('[custom-cart] could not save the coupon attribute:', error);
    }
  }

  /* -------------------------------------------------------------- checkout */

  goToCheckout() {
    const button = this.querySelector('[data-cc-checkout]');
    button?.classList.add('is-busy');

    // Shopflo owns checkout. Its own opener is used when present so its flow is
    // unchanged; the plain checkout URL is the fallback.
    if (typeof window.handleFloCheckoutBtn === 'function') {
      window.handleFloCheckoutBtn();
      window.setTimeout(() => button?.classList.remove('is-busy'), 1200);
      return;
    }
    window.location.href = `${this.routes}checkout`;
  }

  /* ----------------------------------------------------------------- money */

  /**
   * Mirrors _formatMoney in assets/main-product-section.js so amounts read
   * identically across the theme.
   */
  money(paise) {
    if (paise == null) return '';
    const format = this.dataset.moneyFormat
      || window.Shopify?.money_format
      || '&#8377;{{amount_no_decimals_with_comma_separator}}';

    /*
     * A whole number of rupees is shown without decimals, a part-rupee with two:
     * ₹230 rather than ₹230.00, but ₹230.30 in full. The test is on the paise, which
     * are always integers, so it is exact — dividing first would invite a
     * floating-point argument about what counts as whole.
     *
     * This is where the cart parts company with _formatMoney in
     * assets/main-product-section.js, which always renders two decimals. Placeholders
     * that ask for no decimals still get none; only the ones carrying decimals become
     * conditional.
     */
    const amount = Math.round(paise);
    const isWholeRupees = amount % 100 === 0;
    const value = amount / 100;
    const rounded = Math.round(value);
    const decimals = isWholeRupees ? 0 : 2;

    // Grouped, not toFixed: a bare toFixed drops the thousands separator, which is why
    // JavaScript-rendered figures read "1198.00" while the Liquid ones read "1,198.00".
    const grouped = value.toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    const formatted = format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, rounded.toLocaleString('en-IN'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, grouped)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(rounded))
      .replace(/\{\{\s*amount\s*\}\}/g, grouped);

    return CustomCartDrawer.decodeEntities(formatted);
  }

  /** Fill {{ placeholders }} the way Liquid's `t` filter would. */
  t(key, replacements = {}) {
    let text = this.strings[key] ?? '';
    for (const [name, value] of Object.entries(replacements)) {
      text = text.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), value);
    }
    return text;
  }

  /**
   * Liquid's `t` filter HTML-escapes any translation whose key does not end in
   * `_html`, so a string like "with '{{ code }}'" arrives as "with &#39;...&#39;".
   * Since these values are written with textContent, the entities have to be decoded
   * here or they show literally. Decoding every value keeps apostrophes, quotes and
   * ampersands working in any future translation.
   */
  static readStrings(raw) {
    try {
      const parsed = JSON.parse(raw ?? '{}');
      return Object.fromEntries(
        Object.entries(parsed).map(([key, value]) => [
          key,
          typeof value === 'string' ? CustomCartDrawer.decodeEntities(value) : value,
        ]),
      );
    } catch (error) {
      console.error('[custom-cart] strings attribute is not valid JSON:', error);
      return {};
    }
  }

  static decodeEntities(text) {
    const area = document.createElement('textarea');
    area.innerHTML = text;
    return area.value;
  }
}

if (!customElements.get('custom-cart-drawer')) {
  customElements.define('custom-cart-drawer', CustomCartDrawer);
}
